import {CircularProgress, List, ListItem, ListItemText, Switch, FormControlLabel} from '@mui/material';
import {useState, useEffect, useContext} from 'react';
import {FacilityType, YardLayoutType, YardSpotType, WarehouseType} from '../../types/Facility';
import {UserContext} from '../../App';
import {Modal} from '@mui/material';
import {Close, Check, Add, Delete, Edit, ClearAll} from '@mui/icons-material';
import Select from 'react-select';
import {twMerge} from 'tailwind-merge';
import gate1 from '../../assets/gate1.png';
import gate2 from '../../assets/gate2.png';
import YardManagement from '../YardManagement/YardManagement';
import WarehouseManagement from '../WarehouseManagement/WarehouseManagement';
import {isSuperAdmin, isAdmin} from '../../utils/roleHelper';

import { getAuthToken } from '../../utils/cookieUtils';
// Environment variables
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

const Facility = () => {
    // @ts-ignore
    const { user } = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [facility, setFacility] = useState<FacilityType | null>(null);
    const [showGateModal, setShowGateModal] = useState(false);
    const [showGateSuccess, setShowGateSuccess] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [gate, setGate] = useState('');
    const [pin, setPin] = useState('');
    const [yardLayouts, setYardLayouts] = useState<YardLayoutType[]>([]);
    const [selectedLayout, setSelectedLayout] = useState<string | null>(null);
    const [loadingYardLayout, setLoadingYardLayout] = useState(false);
    const [editingYardLayout, setEditingYardLayout] = useState(false);
    const [editedYardLayout, setEditedYardLayout] = useState<YardLayoutType | null>(null);
    const [showEditYardSpotModal, setShowEditYardSpotModal] = useState(false);
    const [currentYardSpot, setCurrentYardSpot] = useState<YardSpotType | null>(null);
    const [showAddYardSpotModal, setShowAddYardSpotModal] = useState(false);
    const [newYardSpot, setNewYardSpot] = useState<Partial<YardSpotType>>({});
    
    // Warehouse management state
    const [warehouse, setWarehouse] = useState<WarehouseType | null>(null);
    const [loadingWarehouse, setLoadingWarehouse] = useState(false);
    
    // Facility edit state
    const [showFacilityEditModal, setShowFacilityEditModal] = useState(false);
    const [editedFacility, setEditedFacility] = useState<FacilityType | null>(null);
    const [allowedScacs, setAllowedScacs] = useState<string[]>([]);
    const [newScac, setNewScac] = useState('');
    const [timezone, setTimezone] = useState<string>('');
    
    // Cache busting state
    const [purgingCache, setPurgingCache] = useState(false);
    
    // Import data modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [generalCsvFile, setGeneralCsvFile] = useState<File | null>(null);
    const [trailersCsvFile, setTrailersCsvFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    
    // Job tracking state
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [currentJobId, setCurrentJobId] = useState<string | null>(null);
    const [jobStatus, setJobStatus] = useState<any>(null);
    const [pollingInterval, setPollingInterval] = useState<NodeJS.Timeout | null>(null);
    
    // Wizard state
    const [importStep, setImportStep] = useState(1); // 1: upload, 2: configuration, 3: field mapping
    const [generalCsvHeaders, setGeneralCsvHeaders] = useState<string[]>([]);
    const [trailersCsvHeaders, setTrailersCsvHeaders] = useState<string[]>([]);
    const [generalSampleData, setGeneralSampleData] = useState<string[][]>([]);
    const [trailersSampleData, setTrailersSampleData] = useState<string[][]>([]);
    const [purgeExistingData, setPurgeExistingData] = useState(false);
    const [generalDataStartLine, setGeneralDataStartLine] = useState(1);
    const [trailersDataStartLine, setTrailersDataStartLine] = useState(1);
    
    // Configuration options (Step 2)
    const [trailerNameFormat, setTrailerNameFormat] = useState<'compound' | 'separate'>('compound');
    const [dateFormat, setDateFormat] = useState<'MM/DD/YYYY HH:mm' | 'DD/MM/YYYY HH:mm' | 'YYYY-MM-DD HH:mm' | 'MM-DD-YYYY HH:mm'>('MM/DD/YYYY HH:mm');
    const [loadStatusMappings, setLoadStatusMappings] = useState<{[key: string]: string}>({
        'LOADING': 'LOADING STARTED',
        'UNLOADING': 'UNLOADING STARTED',
        'EMPTY': 'EMPTY',
        'LOADED': 'LOADED'
    });
    const [availableLocationTypes, setAvailableLocationTypes] = useState<string[]>([]);
    const [stagingAreaLocationType, setStagingAreaLocationType] = useState<string>('');
    const [unknownLocationType, setUnknownLocationType] = useState<string>('Temporary');
    const [trailerFieldMappings, setTrailerFieldMappings] = useState({
        trailer_name: '', // Used for compound format (SCAC-TrailerNumber)
        scac: '',         // Used for separate format
        trailer_number: '', // Used for separate format
        last_move_date: '',
        trailer_check_in_date: '',
        location: '',
        location_type: '',
        reference_number: '',
        load_status: ''
    });
    const [structureFieldMappings, setStructureFieldMappings] = useState({
        location: '',
        location_type: ''
    });

    // Track auto-applied mappings for visual feedback
    const [autoAppliedTrailerMappings, setAutoAppliedTrailerMappings] = useState<string[]>([]);
    const [autoAppliedStructureMappings, setAutoAppliedStructureMappings] = useState<string[]>([]);

    // Empty drop toggle state
    const [emptyDropEnabled, setEmptyDropEnabled] = useState<boolean>(true);
    const [togglingEmptyDrop, setTogglingEmptyDrop] = useState(false);
    const [emptyDropError, setEmptyDropError] = useState('');

    // Workflow kiosks toggle state
    const [workflowKiosksActive, setWorkflowKiosksActive] = useState<boolean>(true);
    const [togglingWorkflowKiosks, setTogglingWorkflowKiosks] = useState(false);
    const [workflowKiosksError, setWorkflowKiosksError] = useState('');

    // Check if user is a super admin
    const userIsSuperAdmin = isSuperAdmin(user);
    const userIsAdmin = isAdmin(user);
    // Function to get the currently selected yard layout
    const getCurrentYardLayout = (): YardLayoutType | null => {
        if (!selectedLayout || !yardLayouts.length) return null;
        return yardLayouts.find(layout => layout.uuid === selectedLayout) || null;
    };

    useEffect(() => {
        const fetchFacility = async () => {
            setLoading(true);
            try {
                // Extract facility ID from URL
                const facilityId = window.location.pathname.split('/').pop();
                
                // GraphQL query for facility
                const facilityQuery = `
                    query {
                        facility(id: "${facilityId || "1"}") {
                            id
                            name
                            configuration
                            activation {
                                yms
                                empty_drop_enabled
                                workflow_kiosks_active
                            }
                        }
                    }
                `;

                // Get auth token from cookie
                const authToken = getAuthToken();

                // Fetch facility using GraphQL
                const response = await fetch(GRAPHQL_API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        query: facilityQuery
                    })
                });

                const result = await response.json();
                
                if (result.errors) {
                    throw new Error(result.errors[0].message);
                }

                setFacility(result.data.facility);

                // Sync empty drop enabled state from activation data
                if (result.data.facility?.activation?.empty_drop_enabled !== undefined) {
                    setEmptyDropEnabled(result.data.facility.activation.empty_drop_enabled);
                }

                // Sync workflow kiosks active state from activation data
                if (result.data.facility?.activation?.workflow_kiosks_active !== undefined) {
                    setWorkflowKiosksActive(result.data.facility.activation.workflow_kiosks_active);
                }

                // Fetch yard layout and warehouse data after getting facility data
                if (result.data.facility?.id) {
                    fetchYardLayout(result.data.facility.id);
                    fetchWarehouseData(result.data.facility.id);
                }
            } catch (error) {
                console.error(error);
                setError('An error occurred while fetching the facility');
                // Fallback to mock data for development
                // setFacility({
                //     id: '123',
                //     name: 'Aurora',
                //     address: '330 E. Liberty, Ann Arbor, MI 48104',
                //     gate_count: 2
                // });
            }
            setLoading(false);
        };
        fetchFacility();
    }, []);

    // Extract location types when entering Step 3 (Field Mapping) if location_type is already mapped
    useEffect(() => {
        if (importStep === 3) {
            const hasLocationTypeMapping = 
                (trailersCsvFile && trailerFieldMappings.location_type) ||
                (generalCsvFile && structureFieldMappings.location_type);
            
            if (hasLocationTypeMapping) {
                updateAvailableLocationTypes();
            }
        }
    }, [importStep]);

    const fetchYardLayout = async (facilityId: string) => {
        setLoadingYardLayout(true);
        try {
            // GraphQL query for yard layout with expanded fields
            const yardLayoutQuery = `
                query GetYardLayout {
                    yard_layout(facility_id: "${facilityId}") {
                        uuid
                        name
                        description
                        facility_id
                        depth
                        parentZoneUuid
                        configuration 
                        zone_type
                        yard_spots {
                            uuid
                            name
                            facility_zone_uuid
                            order_number
                            configuration 
                        }
                    }
                }
            `;

            // Get auth token from cookie
            const authToken = getAuthToken();

            // Fetch yard layout using GraphQL
            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    query: yardLayoutQuery
                })
            });

            const result = await response.json();
            
            if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            // Handle array of yard layouts
            if (Array.isArray(result.data.yard_layout)) {
                setYardLayouts(result.data.yard_layout);
                
                // Look for "Main Yard" specifically
                const mainYard = result.data.yard_layout.find((layout: YardLayoutType) => layout.name === "Main Yard");
                if (mainYard) {
                    setSelectedLayout(mainYard.uuid);
                    setEditedYardLayout(mainYard);
                } else {
                    // If no "Main Yard" found (whether array is empty or has other yards), set error
                    setError('Facility zones cannot be managed without proper account setup. Please contact support to configure a "Main Yard".');
                }
            } else {
                // Handle single yard layout (just in case)
                const singleLayout = result.data.yard_layout;
                setYardLayouts([singleLayout]);
                
                if (singleLayout.name === "Main Yard") {
                    setSelectedLayout(singleLayout.uuid);
                    setEditedYardLayout(singleLayout);
                } else {
                    setError('Facility zones cannot be managed without proper account setup. Please contact support to configure a "Main Yard".');
                }
            }
        } catch (error) {
            console.error('Error fetching yard layout:', error);
            setError('An error occurred while fetching yard layout');
        }
        setLoadingYardLayout(false);
    };

    const fetchWarehouseData = async (facilityId: string) => {
        setLoadingWarehouse(true);
        try {
            // GraphQL query for warehouse data
            const warehouseQuery = `
                query GetWarehouseByFacility {
                    warehouse(facilityId: "${facilityId}") {
                        facilityId
                        warehouseZones {
                            zoneUuid
                            zoneName
                            configuration
                            docks {
                                dockUuid
                                dockName
                                dockStatus
                                configuration
                                order_number
                            }
                        }
                    }
                }
            `;

            // Get auth token from cookie
            const authToken = getAuthToken();

            console.log('Fetching warehouse data for facility:', facilityId);

            // Fetch warehouse data using GraphQL
            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    query: warehouseQuery
                })
            });

            const result = await response.json();
            
            console.log('Warehouse API response:', result);
            
            if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            if (result.data && result.data.warehouse) {
                console.log('Setting warehouse data:', result.data.warehouse);
                setWarehouse(result.data.warehouse);
            } else {
                console.log('No warehouse data returned from API');
                setWarehouse(null);
            }
        } catch (error) {
            console.error('Error fetching warehouse data:', error);
            setError('An error occurred while fetching warehouse data');
            setWarehouse(null);
        }
        setLoadingWarehouse(false);
    };

    const handleGateModalClose = () => {
        setShowGateModal(false);
    };

    const handleOpenGate = () => {
        setShowGateSuccess(true);
        setTimeout(() => {
            setShowGateSuccess(false);
            setGate('');
            setShowGateModal(false);
        }, 2000);
    };

    const handlePinModalClose = () => {
        setShowPinModal(false);
        setPin('');
        setGate('');
    };

    const handleGeneratePin = () => {
        setPin(Math.floor(1000 + Math.random() * 9000).toString());
    };

    const handleEditYardLayout = () => {
        const currentLayout = getCurrentYardLayout();
        if (currentLayout) {
            setEditedYardLayout({...currentLayout});
            setEditingYardLayout(true);
        }
    };

    const handleSaveYardLayout = async () => {
        if (!editedYardLayout || !selectedLayout) return;
        
        // Update the layout in the array
        const updatedLayouts = yardLayouts.map(layout => 
            layout.uuid === selectedLayout ? editedYardLayout : layout
        );
        
        setYardLayouts(updatedLayouts);
        setEditingYardLayout(false);
        
        // Mock implementation of saving to API
        console.log('Saving yard layout:', editedYardLayout);
    };

    const handleCancelEditYardLayout = () => {
        // Reset edited yard layout to the current layout
        const currentLayout = getCurrentYardLayout();
        if (currentLayout) {
            setEditedYardLayout({...currentLayout});
        }
        setEditingYardLayout(false);
        window.location.reload();
    };

    const handleYardLayoutNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (editedYardLayout) {
            setEditedYardLayout({
                ...editedYardLayout,
                name: e.target.value
            });
        }
    };

    const handleYardLayoutDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (editedYardLayout) {
            setEditedYardLayout({
                ...editedYardLayout,
                description: e.target.value
            });
        }
    };

    const handleEditYardSpot = (spot: YardSpotType) => {
        setCurrentYardSpot(spot);
        setShowEditYardSpotModal(true);
    };

    const handleEditYardSpotClose = () => {
        setShowEditYardSpotModal(false);
        setCurrentYardSpot(null);
    };

    const handleYardSpotNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (currentYardSpot) {
            setCurrentYardSpot({
                ...currentYardSpot,
                name: e.target.value
            });
        }
    };

    const handleSaveYardSpot = () => {
        if (currentYardSpot && editedYardLayout) {
            const updatedSpots = editedYardLayout.yard_spots?.map(spot => 
                spot.uuid === currentYardSpot.uuid ? currentYardSpot : spot
            ) || [];
            
            setEditedYardLayout({
                ...editedYardLayout,
                yard_spots: updatedSpots
            });
            
            setShowEditYardSpotModal(false);
            setCurrentYardSpot(null);
        }
    };

    const handleDeleteYardSpot = (spotUuid: string) => {
        if (editedYardLayout) {
            const updatedSpots = editedYardLayout.yard_spots?.filter(
                spot => spot.uuid !== spotUuid
            ) || [];
            
            setEditedYardLayout({
                ...editedYardLayout,
                yard_spots: updatedSpots
            });
        }
    };

    const handleAddYardSpot = () => {
        setNewYardSpot({
            name: '',
            facility_id: facility?.id || '',
            facility_zone_uuid: ''
        });
        setShowAddYardSpotModal(true);
    };

    const handleAddYardSpotClose = () => {
        setShowAddYardSpotModal(false);
        setNewYardSpot({});
    };

    const handleNewYardSpotNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewYardSpot({
            ...newYardSpot,
            name: e.target.value
        });
    };

    const handleNewYardSpotZoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNewYardSpot({
            ...newYardSpot,
            facility_zone_uuid: e.target.value
        });
    };

    const handleSaveNewYardSpot = () => {
        if (editedYardLayout && newYardSpot.name && newYardSpot.facility_zone_uuid) {
            const newSpot: YardSpotType = {
                uuid: `new-${Date.now()}`, // Generate a temporary UUID
                name: newYardSpot.name,
                facility_id: facility?.id || '',
                facility_zone_uuid: newYardSpot.facility_zone_uuid
            };
            
            setEditedYardLayout({
                ...editedYardLayout,
                yard_spots: [...(editedYardLayout.yard_spots || []), newSpot]
            });
            
            setShowAddYardSpotModal(false);
            setNewYardSpot({});
        }
    };

    // Facility edit handlers
    const handleFacilityEditOpen = () => {
        if (facility) {
            setEditedFacility({...facility});
            
            // Load allowed_scacs and timezone from facility configuration
            try {
                const config = facility.configuration ? 
                    (typeof facility.configuration === 'string' ? 
                        JSON.parse(facility.configuration) : facility.configuration) : {};
                setAllowedScacs(config.allowed_scacs || []);
                setTimezone(config.timezone || '');
            } catch (error) {
                console.error('Error parsing facility configuration:', error);
                setAllowedScacs([]);
                setTimezone('');
            }
            
            setShowFacilityEditModal(true);
        }
    };

    const handleFacilityEditClose = () => {
        setShowFacilityEditModal(false);
        setEditedFacility(null);
        setAllowedScacs([]);
        setNewScac('');
        setTimezone('');
        window.location.reload();
    };

    const handleFacilityEditSave = async () => {
        if (!editedFacility) return;

        try {
            setLoading(true);
            
            // Prepare the configuration object
            const existingConfig = editedFacility.configuration ? 
                (typeof editedFacility.configuration === 'string' ? 
                    JSON.parse(editedFacility.configuration) : editedFacility.configuration) : {};
            
            const updatedConfig = {
                ...existingConfig,
                allowed_scacs: allowedScacs,
                ...(timezone && { timezone: timezone })
            };

            const configurationString = JSON.stringify(updatedConfig);
            
            // Debug logging
            console.log('Allowed SCACs array:', allowedScacs);
            console.log('Updated config object:', updatedConfig);
            console.log('Configuration string:', configurationString);

            // GraphQL mutation to update facility (following WarehouseManagement pattern)
            const updateFacilityMutation = `
                mutation UpdateFacility($id: ID!, $input: FacilityUpdateInput!) {
                    update_facility(id: $id, input: $input) {
                        id
                        name
                        configuration
                    }
                }
            `;

            // Get auth token from cookie
            const authToken = getAuthToken();

            const mutationPayload = {
                query: updateFacilityMutation,
                variables: {
                    id: editedFacility.id,
                    input: {
                        name: editedFacility.name,
                        configuration: configurationString
                    }
                }
            };

            // Debug the full payload
            console.log('GraphQL mutation payload:', JSON.stringify(mutationPayload, null, 2));

            // Make the API call to update the facility
            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify(mutationPayload)
            });

            const result = await response.json();
            
            if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            // Update local state with the response from the API
            if (result.data && result.data.update_facility) {
                setFacility(result.data.update_facility);
                console.log('Facility updated successfully:', result.data.update_facility);
            }
            
            handleFacilityEditClose();
        } catch (error) {
            console.error('Error saving facility:', error);
            setError('Failed to save facility changes. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleAddScac = () => {
        if (newScac.trim() && !allowedScacs.includes(newScac.trim().toUpperCase())) {
            setAllowedScacs([...allowedScacs, newScac.trim().toUpperCase()]);
            setNewScac('');
        }
    };

    const handleRemoveScac = (scacToRemove: string) => {
        setAllowedScacs(allowedScacs.filter(scac => scac !== scacToRemove));
    };

    const handleScacInputKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddScac();
        }
    };

    // Handle page reload for modal cancels
    const handleModalCancel = () => {
        window.location.reload();
    };

    // Handle cache purging
    const handlePurgeCache = async () => {
        if (!facility?.id) return;
        
        setPurgingCache(true);
        try {
            const purgeCacheMutation = `
                mutation PurgeFacilityCache($facilityId: String!) {
                    purgeFacilityCache(facilityId: $facilityId) {
                        success
                        message
                        facilityId
                    }
                }
            `;

            // Get auth token from cookie
            const authToken = getAuthToken();

            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    query: purgeCacheMutation,
                    variables: {
                        facilityId: facility.id
                    }
                })
            });

            const result = await response.json();
            
            if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            if (result.data?.purgeFacilityCache?.success) {
                alert('Cache purged successfully!');
            } else {
                alert('Failed to purge cache: ' + (result.data?.purgeFacilityCache?.message || 'Unknown error'));
            }
        } catch (error) {
            console.error('Error purging cache:', error);
            alert('Error purging cache. Please try again.');
        } finally {
            setPurgingCache(false);
        }
    };

    const handleToggleEmptyDrop = async (enabled: boolean) => {
        if (!facility?.id) return;

        setTogglingEmptyDrop(true);
        setEmptyDropError('');

        try {
            const authToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('authToken='))
                ?.split('=')[1];

            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    query: `
                        mutation SetEmptyDropEnabled($facilityId: Int!, $enabled: Boolean!) {
                            set_empty_drop_enabled(facility_id: $facilityId, enabled: $enabled) {
                                uuid
                                facility_id
                                yms
                                empty_drop_enabled
                            }
                        }
                    `,
                    variables: {
                        facilityId: parseInt(facility.id),
                        enabled
                    }
                })
            });

            const result = await response.json();

            if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            setEmptyDropEnabled(result.data.set_empty_drop_enabled.empty_drop_enabled);
        } catch (error: any) {
            console.error('Error toggling empty drop:', error);
            setEmptyDropError(error.message || 'Failed to update empty drop setting');
            // Revert the optimistic update
            setEmptyDropEnabled(!enabled);
        } finally {
            setTogglingEmptyDrop(false);
        }
    };

    const handleToggleWorkflowKiosks = async (enabled: boolean) => {
        if (!facility?.id) return;

        setTogglingWorkflowKiosks(true);
        setWorkflowKiosksError('');

        try {
            const authToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('authToken='))
                ?.split('=')[1];

            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    query: `
                        mutation SetWorkflowKiosksActive($facilityId: Int!, $enabled: Boolean!) {
                            set_workflow_kiosks_active(facility_id: $facilityId, enabled: $enabled) {
                                uuid
                                facility_id
                                workflow_kiosks_active
                            }
                        }
                    `,
                    variables: {
                        facilityId: parseInt(facility.id),
                        enabled
                    }
                })
            });

            const result = await response.json();

            if (result.errors) {
                throw new Error(result.errors[0].message);
            }

            setWorkflowKiosksActive(result.data.set_workflow_kiosks_active.workflow_kiosks_active);
        } catch (error: any) {
            console.error('Error toggling workflow kiosks:', error);
            setWorkflowKiosksError(error.message || 'Failed to update workflow kiosks setting');
            setWorkflowKiosksActive(!enabled);
        } finally {
            setTogglingWorkflowKiosks(false);
        }
    };

    // Extract unique location types from CSV data
    const extractLocationTypes = (headers: string[], sampleData: string[][], fullCsvContent: string, startLine: number, locationTypeColumn: string): string[] => {
        if (!locationTypeColumn) return [];
        
        const columnIndex = headers.indexOf(locationTypeColumn);
        if (columnIndex === -1) return [];
        
        const uniqueTypes = new Set<string>();
        
        // Parse all rows from the CSV to get all unique location types
        const lines = fullCsvContent.split('\n');
        for (let i = startLine; i < lines.length; i++) {
            const line = lines[i].trim();
            if (!line) continue;
            
            const columns = line.split(',').map(col => col.trim().replace(/"/g, ''));
            if (columns[columnIndex]) {
                uniqueTypes.add(columns[columnIndex]);
            }
        }
        
        return Array.from(uniqueTypes).sort();
    };

    // Update location types when CSV data changes
    const updateAvailableLocationTypes = async () => {
        const allTypes = new Set<string>();
        
        try {
            // Get location types from general CSV
            if (generalCsvFile && structureFieldMappings.location_type) {
                const fileContent = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target?.result as string);
                    reader.onerror = reject;
                    reader.readAsText(generalCsvFile);
                });
                
                const types = extractLocationTypes(
                    generalCsvHeaders,
                    generalSampleData,
                    fileContent,
                    generalDataStartLine,
                    structureFieldMappings.location_type
                );
                types.forEach(type => allTypes.add(type));
            }
            
            // Get location types from trailers CSV
            if (trailersCsvFile && trailerFieldMappings.location_type) {
                const fileContent = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target?.result as string);
                    reader.onerror = reject;
                    reader.readAsText(trailersCsvFile);
                });
                
                const types = extractLocationTypes(
                    trailersCsvHeaders,
                    trailersSampleData,
                    fileContent,
                    trailersDataStartLine,
                    trailerFieldMappings.location_type
                );
                types.forEach(type => allTypes.add(type));
            }
        } catch (error) {
            console.error('Error extracting location types:', error);
        }
        
        const sortedTypes = Array.from(allTypes).sort();
        setAvailableLocationTypes(sortedTypes);
        
        // Auto-select first location type as staging area if not already set
        if (sortedTypes.length > 0 && !stagingAreaLocationType) {
            setStagingAreaLocationType(sortedTypes[0]);
        }
    };

    // CSV parsing utility
    const parseCsvHeaders = (file: File, startLine: number = 1): Promise<string[]> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csv = e.target?.result as string;
                    const lines = csv.split('\n');
                    const headerLineIndex = startLine - 1; // Convert to 0-based index
                    
                    if (lines.length > headerLineIndex && headerLineIndex >= 0) {
                        const headers = lines[headerLineIndex].split(',').map(header => header.trim().replace(/"/g, ''));
                        resolve(headers);
                    } else {
                        resolve([]);
                    }
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    // Parse CSV headers and sample data
    const parseCsvHeadersAndSample = (file: File, startLine: number = 1): Promise<{headers: string[], sampleData: string[][]}> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csv = e.target?.result as string;
                    const lines = csv.split('\n');
                    const headerLineIndex = startLine - 1; // Convert to 0-based index
                    
                    if (lines.length > headerLineIndex && headerLineIndex >= 0) {
                        const headers = lines[headerLineIndex].split(',').map(header => header.trim().replace(/"/g, ''));
                        
                        // Get up to 2 sample data rows after the header
                        const sampleData: string[][] = [];
                        for (let i = headerLineIndex + 1; i < Math.min(lines.length, headerLineIndex + 3); i++) {
                            if (lines[i].trim()) {
                                const row = lines[i].split(',').map(cell => cell.trim().replace(/"/g, ''));
                                sampleData.push(row);
                            }
                        }
                        
                        resolve({ headers, sampleData });
                    } else {
                        resolve({ headers: [], sampleData: [] });
                    }
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    // Intelligent header detection
    const detectHeaderLine = (file: File): Promise<{line: number, confidence: number, reason: string}> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const csv = e.target?.result as string;
                    const lines = csv.split('\n').slice(0, 10); // Analyze first 10 lines
                    
                    let bestLine = 1;
                    let bestScore = 0;
                    let bestReason = '';

                    for (let i = 0; i < lines.length; i++) {
                        const line = lines[i].trim();
                        if (!line) continue;

                        const columns = line.split(',').map(col => col.trim().replace(/"/g, ''));
                        let score = 0;
                        let reasons: string[] = [];

                        // Skip lines that are too short
                        if (columns.length < 2) continue;

                        // Score based on various criteria
                        
                        // 1. Non-numeric headers (headers are usually text)
                        const nonNumericCount = columns.filter(col => 
                            col && isNaN(Number(col)) && !col.match(/^\d{4}-\d{2}-\d{2}/)
                        ).length;
                        const nonNumericRatio = nonNumericCount / columns.length;
                        if (nonNumericRatio > 0.7) {
                            score += 30;
                            reasons.push('mostly text fields');
                        }

                        // 2. No empty fields (headers usually don't have empty values)
                        const emptyCount = columns.filter(col => !col || col.trim() === '').length;
                        if (emptyCount === 0) {
                            score += 20;
                            reasons.push('no empty fields');
                        }

                        // 3. Consistent formatting (headers tend to be consistent)
                        const hasConsistentCase = columns.every(col => 
                            col === col.toLowerCase() || col === col.toUpperCase() || 
                            col.includes('_') || col.includes(' ')
                        );
                        if (hasConsistentCase) {
                            score += 15;
                            reasons.push('consistent formatting');
                        }

                        // 4. Common header patterns
                        const headerPatterns = [
                            /^(id|name|type|status|date|time|location|zone|dock|yard|trailer|scac|reference|load)/i,
                            /_(id|name|type|status|date|time|location|zone|dock|yard|trailer|scac|reference|load)$/i,
                            /^(is_|has_)/i
                        ];
                        const patternMatches = columns.filter(col => 
                            headerPatterns.some(pattern => pattern.test(col))
                        ).length;
                        if (patternMatches > 0) {
                            score += patternMatches * 10;
                            reasons.push(`${patternMatches} common header patterns`);
                        }

                        // 5. No date patterns (data rows often have dates, headers don't)
                        const datePatterns = [
                            /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
                            /^\d{1,2}\/\d{1,2}\/\d{4}/, // MM/DD/YYYY
                            /^\d{1,2}-\d{1,2}-\d{4}/ // MM-DD-YYYY
                        ];
                        const hasDatePattern = columns.some(col => 
                            datePatterns.some(pattern => pattern.test(col))
                        );
                        if (!hasDatePattern) {
                            score += 10;
                            reasons.push('no date patterns');
                        }

                        // 6. Prefer earlier lines (headers are usually at the top)
                        if (i < 3) {
                            score += (3 - i) * 5;
                            reasons.push('early in file');
                        }

                        // Check if this line has the best score
                        if (score > bestScore) {
                            bestScore = score;
                            bestLine = i + 1;
                            bestReason = reasons.join(', ');
                        }
                    }

                    // Calculate confidence percentage
                    const maxPossibleScore = 100; // Rough estimate of max possible score
                    const confidence = Math.min(Math.round((bestScore / maxPossibleScore) * 100), 100);

                    resolve({
                        line: bestLine,
                        confidence,
                        reason: bestReason || 'default to line 1'
                    });
                } catch (error) {
                    reject(error);
                }
            };
            reader.onerror = reject;
            reader.readAsText(file);
        });
    };

    // Reset all import-related state
    const resetImportState = () => {
        setImportStep(1);
        setGeneralCsvFile(null);
        setTrailersCsvFile(null);
        setGeneralCsvHeaders([]);
        setTrailersCsvHeaders([]);
        setGeneralSampleData([]);
        setTrailersSampleData([]);
        setPurgeExistingData(false);
        setGeneralDataStartLine(1);
        setTrailersDataStartLine(1);
        setTrailerNameFormat('compound');
        setDateFormat('MM/DD/YYYY HH:mm');
        setLoadStatusMappings({
            'LOADING': 'LOADING STARTED',
            'UNLOADING': 'UNLOADING STARTED',
            'EMPTY': 'EMPTY',
            'LOADED': 'LOADED'
        });
        setAvailableLocationTypes([]);
        setStagingAreaLocationType('');
        setUnknownLocationType('Temporary');
        setTrailerFieldMappings({
            trailer_name: '',
            scac: '',
            trailer_number: '',
            last_move_date: '',
            trailer_check_in_date: '',
            location: '',
            location_type: '',
            reference_number: '',
            load_status: ''
        });
        setStructureFieldMappings({
            location: '',
            location_type: ''
        });
        setAutoAppliedTrailerMappings([]);
        setAutoAppliedStructureMappings([]);
        
        // Clean up job tracking
        if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
        }
        setCurrentJobId(null);
        setJobStatus(null);
    };

    // Handle import modal
    const handleImportModalClose = () => {
        setShowImportModal(false);
        resetImportState();
    };

    const handleGeneralCsvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setGeneralCsvFile(file);
        if (file) {
            try {
                // Auto-detect headers with the file directly
                await handleAutoDetectGeneralHeaders(file);
            } catch (error) {
                console.error('Error parsing CSV headers:', error);
                setGeneralCsvHeaders([]);
                setGeneralSampleData([]);
            }
        } else {
            setGeneralCsvHeaders([]);
            setGeneralSampleData([]);
        }
    };

    const handleTrailersCsvChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] || null;
        setTrailersCsvFile(file);
        if (file) {
            try {
                // Auto-detect headers with the file directly
                await handleAutoDetectTrailersHeaders(file);
            } catch (error) {
                console.error('Error parsing CSV headers:', error);
                setTrailersCsvHeaders([]);
                setTrailersSampleData([]);
            }
        } else {
            setTrailersCsvHeaders([]);
            setTrailersSampleData([]);
        }
    };

    // Handle data start line changes
    const handleGeneralDataStartLineChange = async (newStartLine: number) => {
        setGeneralDataStartLine(newStartLine);
        if (generalCsvFile && newStartLine > 0) {
            try {
                const { headers, sampleData } = await parseCsvHeadersAndSample(generalCsvFile, newStartLine);
                setGeneralCsvHeaders(headers);
                setGeneralSampleData(sampleData);
                
                // Apply saved mappings if available
                const savedData = loadFieldMappingsFromStorage();
                if (savedData?.structure) {
                    const suggestions = suggestFieldMappings(headers, savedData.structure);
                    if (Object.keys(suggestions).length > 0) {
                        setStructureFieldMappings(prev => ({ ...prev, ...suggestions }));
                        setAutoAppliedStructureMappings(Object.keys(suggestions));
                        console.log('Applied saved structure field mappings after line change:', suggestions);
                    }
                }
            } catch (error) {
                console.error('Error parsing CSV headers:', error);
                setGeneralCsvHeaders([]);
                setGeneralSampleData([]);
            }
        }
    };

    const handleTrailersDataStartLineChange = async (newStartLine: number) => {
        setTrailersDataStartLine(newStartLine);
        if (trailersCsvFile && newStartLine > 0) {
            try {
                const { headers, sampleData } = await parseCsvHeadersAndSample(trailersCsvFile, newStartLine);
                setTrailersCsvHeaders(headers);
                setTrailersSampleData(sampleData);
                
                // Apply saved mappings if available
                const savedData = loadFieldMappingsFromStorage();
                if (savedData?.trailer) {
                    const suggestions = suggestFieldMappings(headers, savedData.trailer, true);
                    if (Object.keys(suggestions).length > 0) {
                        setTrailerFieldMappings(prev => ({ ...prev, ...suggestions }));
                        setAutoAppliedTrailerMappings(Object.keys(suggestions));
                        console.log('Applied saved trailer field mappings after line change:', suggestions);
                    }
                }
                
                // Apply saved trailer name format
                if (savedData?.trailerFormat) {
                    setTrailerNameFormat(savedData.trailerFormat);
                    console.log('Applied saved trailer name format after line change:', savedData.trailerFormat);
                }
                
                // Apply saved date format
                if (savedData?.dateFormat) {
                    setDateFormat(savedData.dateFormat);
                    console.log('Applied saved date format after line change:', savedData.dateFormat);
                }
                
                // Apply saved load status mappings
                if (savedData?.loadStatusMappings) {
                    setLoadStatusMappings(savedData.loadStatusMappings);
                    console.log('Applied saved load status mappings after line change:', savedData.loadStatusMappings);
                }
                
                // Apply saved staging area location type
                if (savedData?.stagingAreaLocationType) {
                    setStagingAreaLocationType(savedData.stagingAreaLocationType);
                    console.log('Applied saved staging area location type after line change:', savedData.stagingAreaLocationType);
                }
                
                // Apply saved unknown location type
                if (savedData?.unknownLocationType) {
                    setUnknownLocationType(savedData.unknownLocationType);
                    console.log('Applied saved unknown location type after line change:', savedData.unknownLocationType);
                }
            } catch (error) {
                console.error('Error parsing CSV headers:', error);
                setTrailersCsvHeaders([]);
                setTrailersSampleData([]);
            }
        }
    };

    // Auto-detect header line for general CSV
    const handleAutoDetectGeneralHeaders = async (fileToUse?: File) => {
        const file = fileToUse || generalCsvFile;
        if (!file) return;
        
        try {
            const detection = await detectHeaderLine(file);
            setGeneralDataStartLine(detection.line);
            
            // Parse headers and sample data from detected line
            const { headers, sampleData } = await parseCsvHeadersAndSample(file, detection.line);
            setGeneralCsvHeaders(headers);
            setGeneralSampleData(sampleData);
            
            // Apply saved mappings if available
            const savedData = loadFieldMappingsFromStorage();
            if (savedData?.structure) {
                const suggestions = suggestFieldMappings(headers, savedData.structure);
                if (Object.keys(suggestions).length > 0) {
                    setStructureFieldMappings(prev => ({ ...prev, ...suggestions }));
                    setAutoAppliedStructureMappings(Object.keys(suggestions));
                    console.log('Applied saved structure field mappings after auto-detect:', suggestions);
                }
            }
            
            // Show detection result to user
        } catch (error) {
            console.error('Error auto-detecting headers:', error);
            alert('Failed to auto-detect headers. Please specify manually.');
        }
    };

    // Auto-detect header line for trailers CSV
    const handleAutoDetectTrailersHeaders = async (fileToUse?: File) => {
        const file = fileToUse || trailersCsvFile;
        if (!file) return;
        
        try {
            const detection = await detectHeaderLine(file);
            setTrailersDataStartLine(detection.line);
            
            // Parse headers and sample data from detected line
            const { headers, sampleData } = await parseCsvHeadersAndSample(file, detection.line);
            setTrailersCsvHeaders(headers);
            setTrailersSampleData(sampleData);
            
            // Apply saved mappings if available
            const savedData = loadFieldMappingsFromStorage();
            if (savedData?.trailer) {
                const suggestions = suggestFieldMappings(headers, savedData.trailer, true);
                if (Object.keys(suggestions).length > 0) {
                    setTrailerFieldMappings(prev => ({ ...prev, ...suggestions }));
                    setAutoAppliedTrailerMappings(Object.keys(suggestions));
                    console.log('Applied saved trailer field mappings after auto-detect:', suggestions);
                }
            }
            
            // Apply saved trailer name format
            if (savedData?.trailerFormat) {
                setTrailerNameFormat(savedData.trailerFormat);
                console.log('Applied saved trailer name format after auto-detect:', savedData.trailerFormat);
            }
            
            // Apply saved date format
            if (savedData?.dateFormat) {
                setDateFormat(savedData.dateFormat);
                console.log('Applied saved date format after auto-detect:', savedData.dateFormat);
            }
            
            // Apply saved load status mappings
            if (savedData?.loadStatusMappings) {
                setLoadStatusMappings(savedData.loadStatusMappings);
                console.log('Applied saved load status mappings after auto-detect:', savedData.loadStatusMappings);
            }
            
            // Apply saved staging area location type
            if (savedData?.stagingAreaLocationType) {
                setStagingAreaLocationType(savedData.stagingAreaLocationType);
                console.log('Applied saved staging area location type after auto-detect:', savedData.stagingAreaLocationType);
            }
            
            // Apply saved unknown location type
            if (savedData?.unknownLocationType) {
                setUnknownLocationType(savedData.unknownLocationType);
                console.log('Applied saved unknown location type after auto-detect:', savedData.unknownLocationType);
            }
            
            // Show detection result to user
        } catch (error) {
            console.error('Error auto-detecting headers:', error);
            alert('Failed to auto-detect headers. Please specify manually.');
        }
    };

    const handleContinueToConfiguration = () => {
        setImportStep(2);
    };

    const handleContinueToMapping = () => {
        setImportStep(3);
    };

    const handleBackToConfiguration = () => {
        setImportStep(2);
    };

    // LocalStorage utilities for field mappings
    const STORAGE_KEY_TRAILER_MAPPINGS = 'freightroll_trailer_field_mappings';
    const STORAGE_KEY_STRUCTURE_MAPPINGS = 'freightroll_structure_field_mappings';

    const saveFieldMappingsToStorage = () => {
        try {
            // Save trailer mappings with timestamp
            const trailerData = {
                mappings: trailerFieldMappings,
                format: trailerNameFormat,
                dateFormat: dateFormat,
                loadStatusMappings: loadStatusMappings,
                stagingAreaLocationType: stagingAreaLocationType,
                unknownLocationType: unknownLocationType,
                timestamp: Date.now()
            };
            localStorage.setItem(STORAGE_KEY_TRAILER_MAPPINGS, JSON.stringify(trailerData));

            // Save structure mappings with timestamp
            const structureData = {
                mappings: structureFieldMappings,
                timestamp: Date.now()
            };
            localStorage.setItem(STORAGE_KEY_STRUCTURE_MAPPINGS, JSON.stringify(structureData));
            
            console.log('Field mappings saved to localStorage');
        } catch (error) {
            console.error('Failed to save field mappings to localStorage:', error);
        }
    };

    const loadFieldMappingsFromStorage = () => {
        const result: any = {};
        
        try {
            // Load trailer mappings
            const savedTrailerData = localStorage.getItem(STORAGE_KEY_TRAILER_MAPPINGS);
            if (savedTrailerData) {
                const trailerData = JSON.parse(savedTrailerData);
                result.trailer = trailerData.mappings || {};
                result.trailerFormat = trailerData.format || 'compound';
                result.dateFormat = trailerData.dateFormat || 'MM/DD/YYYY HH:mm';
                result.loadStatusMappings = trailerData.loadStatusMappings || {
                    'LOADING': 'LOADING STARTED',
                    'UNLOADING': 'UNLOADING STARTED',
                    'EMPTY': 'EMPTY',
                    'LOADED': 'LOADED'
                };
                result.stagingAreaLocationType = trailerData.stagingAreaLocationType || '';
                result.unknownLocationType = trailerData.unknownLocationType || 'Temporary';
                result.trailerTimestamp = trailerData.timestamp;
            }
        } catch (error) {
            console.error('Failed to load trailer mappings from localStorage:', error);
        }

        try {
            // Load structure mappings
            const savedStructureData = localStorage.getItem(STORAGE_KEY_STRUCTURE_MAPPINGS);
            if (savedStructureData) {
                const structureData = JSON.parse(savedStructureData);
                result.structure = structureData.mappings || {};
                result.structureTimestamp = structureData.timestamp;
            }
        } catch (error) {
            console.error('Failed to load structure mappings from localStorage:', error);
        }

        return Object.keys(result).length > 0 ? result : null;
    };

    // Smart mapping suggestions based on column names and saved mappings
    const suggestFieldMappings = (headers: string[], savedMappings: any, isTrailer: boolean = false) => {
        const suggestions: any = {};
        
        if (!savedMappings) return suggestions;

        // First, try exact matches from saved mappings
        Object.entries(savedMappings).forEach(([field, savedColumn]) => {
            if (savedColumn && headers.includes(savedColumn as string)) {
                suggestions[field] = savedColumn;
            }
        });

        // Then, try intelligent matching for unmapped fields
        const unmappedFields = Object.keys(savedMappings).filter(field => !suggestions[field]);
        
        unmappedFields.forEach(field => {
            const bestMatch = findBestColumnMatch(field, headers);
            if (bestMatch) {
                suggestions[field] = bestMatch;
            }
        });

        return suggestions;
    };

    // Find best column match based on field name patterns
    const findBestColumnMatch = (fieldName: string, headers: string[]) => {
        const patterns: { [key: string]: string[] } = {
            trailer_name: ['trailer', 'trailer_id', 'trailer_number', 'unit', 'equipment'],
            scac: ['scac', 'carrier', 'carrier_code'],
            trailer_number: ['number', 'trailer_num', 'unit_number', 'equipment_number'],
            last_move_date: ['date', 'move_date', 'last_move', 'moved', 'timestamp'],
            trailer_check_in_date: ['check_in', 'checkin', 'check_in_date', 'arrival', 'arrival_date', 'entry', 'entry_date'],
            location: ['location', 'current_location', 'position', 'spot', 'dock', 'name', 'location_name', 'spot_name', 'dock_name'],
            location_type: ['type', 'location_type', 'spot_type', 'area_type', 'zone', 'zone_name', 'area', 'section'],
            reference_number: ['reference', 'ref', 'ref_number', 'order', 'load'],
            load_status: ['status', 'load_status', 'state', 'condition']
        };

        const fieldPatterns = patterns[fieldName] || [];
        
        // Find exact matches first
        for (const pattern of fieldPatterns) {
            const exactMatch = headers.find(header => 
                header.toLowerCase() === pattern.toLowerCase()
            );
            if (exactMatch) return exactMatch;
        }

        // Find partial matches
        for (const pattern of fieldPatterns) {
            const partialMatch = headers.find(header => 
                header.toLowerCase().includes(pattern.toLowerCase()) ||
                pattern.toLowerCase().includes(header.toLowerCase())
            );
            if (partialMatch) return partialMatch;
        }

        return null;
    };

    // Helper function to create rich dropdown options with sample data
    const createDropdownOptions = (headers: string[], sampleData: string[][]) => {
        const options: Array<{value: string, label: string, sampleText?: string, isDisabled: boolean}> = [
            { value: '', label: 'Select column...', isDisabled: false }
        ];
        
        headers.forEach((header, index) => {
            const samples = sampleData.map(row => row[index] || '').filter(Boolean).slice(0, 2);
            const sampleText = samples.length > 0 ? samples.join(', ') : 'No data';
            
            options.push({
                value: header,
                label: header,
                sampleText: sampleText,
                isDisabled: false
            });
        });
        
        return options;
    };

    // Custom option component for React Select with sample data
    const CustomOption = ({ data, ...props }: any) => {
        return (
            <div
                {...props.innerProps}
                className={`px-3 py-2 cursor-pointer ${props.isFocused ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
            >
                <div className="font-medium text-gray-900">{data.label}</div>
                {data.sampleText && (
                    <div className="text-xs text-gray-500 mt-1">
                        Sample: {data.sampleText}
                    </div>
                )}
            </div>
        );
    };

    const handleBackToUpload = () => {
        setImportStep(1);
    };

    // Job status polling
    const pollJobStatus = async (jobId: string) => {
        try {
            const authToken = getAuthToken();

            const jobStatusQuery = `
                query ImportJobStatus($jobId: String!) {
                    importJobStatus(jobId: $jobId) {
                        job_id
                        status
                        progress
                        total_records
                        processed_records
                        errors
                        imported_records {
                            file_type
                            records_processed
                            errors
                        }
                        started_at
                        completed_at
                    }
                }
            `;

            const response = await fetch(GRAPHQL_API_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    query: jobStatusQuery,
                    variables: { jobId }
                })
            });

            const result = await response.json();
            
            if (result.errors) {
                console.error('Error fetching job status:', result.errors);
                return null;
            }

            return result.data?.importJobStatus || null;
        } catch (error) {
            console.error('Error polling job status:', error);
            return null;
        }
    };

    // Start job polling
    const startJobPolling = (jobId: string) => {
        const interval = setInterval(async () => {
            const status = await pollJobStatus(jobId);
            if (status) {
                setJobStatus(status);
                
                // Stop polling if job is completed, failed, or cancelled
                if (['completed', 'failed', 'cancelled'].includes(status.status)) {
                    clearInterval(interval);
                    setPollingInterval(null);
                }
            }
        }, 2000); // Poll every 2 seconds

        setPollingInterval(interval);
    };

    // Handle progress modal close
    const handleProgressModalClose = () => {
        setShowProgressModal(false);
        // Reset all import state for next import
        resetImportState();
    };

    // Handle waiting for job completion
    const handleWaitForCompletion = () => {
        // Keep the progress modal open and continue polling
        // User can see real-time progress
    };

    const handleTrailerFieldMappingChange = (field: string, csvColumn: string) => {
        setTrailerFieldMappings(prev => ({
            ...prev,
            [field]: csvColumn
        }));
        
        // If location_type field is mapped, update available location types
        if (field === 'location_type') {
            // Delay slightly to ensure state is updated
            setTimeout(() => updateAvailableLocationTypes(), 100);
        }
    };

    const handleStructureFieldMappingChange = (field: string, csvColumn: string) => {
        setStructureFieldMappings(prev => ({
            ...prev,
            [field]: csvColumn
        }));
        
        // If location_type field is mapped, update available location types
        if (field === 'location_type') {
            // Delay slightly to ensure state is updated
            setTimeout(() => updateAvailableLocationTypes(), 100);
        }
    };

    // Validation: Check if all required fields are mapped
    const areAllRequiredFieldsMapped = () => {
        // Check trailer fields if trailer file is present
        if (trailersCsvFile) {
            const requiredTrailerFields = [
                'last_move_date',
                'trailer_check_in_date',
                'location',
                'location_type',
                'reference_number',
                'load_status'
            ];

            // Also check trailer name fields based on format
            if (trailerNameFormat === 'compound') {
                if (!trailerFieldMappings.trailer_name) return false;
            } else {
                if (!trailerFieldMappings.scac || !trailerFieldMappings.trailer_number) return false;
            }

            // Check other required fields
            for (const field of requiredTrailerFields) {
                if (!trailerFieldMappings[field as keyof typeof trailerFieldMappings]) {
                    return false;
                }
            }
        }

        // Check structure fields if general file is present
        if (generalCsvFile) {
            if (!structureFieldMappings.location || !structureFieldMappings.location_type) {
                return false;
            }
        }

        return true;
    };

    const getMissingFieldsList = () => {
        const missing: string[] = [];

        if (trailersCsvFile) {
            if (trailerNameFormat === 'compound' && !trailerFieldMappings.trailer_name) {
                missing.push('Trailer Name');
            } else if (trailerNameFormat === 'separate') {
                if (!trailerFieldMappings.scac) missing.push('SCAC Code');
                if (!trailerFieldMappings.trailer_number) missing.push('Trailer Number');
            }

            const fieldLabels: { [key: string]: string } = {
                'last_move_date': 'Last Move Date',
                'trailer_check_in_date': 'Trailer Check-In Date',
                'location': 'Location',
                'location_type': 'Location Type',
                'reference_number': 'Reference Number',
                'load_status': 'Load Status'
            };

            Object.entries(fieldLabels).forEach(([key, label]) => {
                if (!trailerFieldMappings[key as keyof typeof trailerFieldMappings]) {
                    missing.push(label);
                }
            });
        }

        if (generalCsvFile) {
            if (!structureFieldMappings.location) missing.push('Structure Location');
            if (!structureFieldMappings.location_type) missing.push('Structure Location Type');
        }

        return missing;
    };

    const handleImportData = async () => {
        if (!generalCsvFile && !trailersCsvFile) {
            alert('Please select at least one CSV file to import.');
            return;
        }

        // Save field mappings to localStorage for future use
        saveFieldMappingsToStorage();

        setImporting(true);
        try {
            // Get auth token from cookie
            const authToken = getAuthToken();

            // Extract facility ID from URL
            const facilityId = window.location.pathname.split('/').pop();

            // Helper function to read file content
            const readFileContent = (file: File): Promise<string> => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (e) => resolve(e.target?.result as string);
                    reader.onerror = reject;
                    reader.readAsText(file);
                });
            };

            // Build imports array with file content
            const imports: any[] = [];

            // Add structure import if file exists
            if (generalCsvFile) {
                const csvContent = await readFileContent(generalCsvFile);
                imports.push({
                    fileType: 'structure',
                    fieldMappings: JSON.stringify(structureFieldMappings),
                    dataStartLine: generalDataStartLine,
                    fileName: generalCsvFile.name,
                    csvContent: csvContent
                });
            }

            // Add trailers import if file exists
            if (trailersCsvFile) {
                const csvContent = await readFileContent(trailersCsvFile);
                imports.push({
                    fileType: 'trailers',
                    fieldMappings: JSON.stringify(trailerFieldMappings),
                    trailerNameFormat: trailerNameFormat,
                    dateFormat: dateFormat,
                    loadStatusMappings: JSON.stringify(loadStatusMappings),
                    dataStartLine: trailersDataStartLine,
                    fileName: trailersCsvFile.name,
                    csvContent: csvContent
                });
            }

            // Real mutation that will be sent to backend
            const importDataMutation = `
                mutation ImportFacilityData(
                    $facilityId: String!, 
                    $imports: [ImportDataInput!]!, 
                    $purgeExistingData: Boolean!,
                    $stagingAreaLocationType: String,
                    $unknownLocationType: String
                ) {
                    importFacilityData(
                        facilityId: $facilityId,
                        imports: $imports,
                        purgeExistingData: $purgeExistingData,
                        stagingAreaLocationType: $stagingAreaLocationType,
                        unknownLocationType: $unknownLocationType
                    ) {
                        success
                        message
                        job_id
                    }
                }
            `;

            // Log the complete structure that would be sent to the backend
            console.log('🚀 Import Data Request Structure (Ready for Backend Implementation):', {
                facilityId,
                purgeExistingData,
                stagingAreaLocationType,
                unknownLocationType,
                imports,
                
                // Summary for easy viewing
                summary: {
                    facilityId,
                    purgeExistingData,
                    stagingAreaLocationType,
                    unknownLocationType,
                    fileCount: imports.length,
                    files: imports.map(imp => ({
                        type: imp.fileType,
                        name: imp.fileName,
                        dataStartLine: imp.dataStartLine,
                        mappingCount: Object.keys(imp.fieldMappings).length,
                        trailerFormat: imp.trailerNameFormat || 'N/A',
                        dateFormat: imp.dateFormat || 'N/A',
                        csvPreview: imp.csvContent?.substring(0, 100) + '...'
                    }))
                }
            });

            // Since the mutation doesn't exist yet, simulate a successful response
            console.log('📝 GraphQL Mutation Needed:', `
mutation ImportFacilityData(
  $facilityId: String!
  $imports: [ImportDataInput!]!
  $purgeExistingData: Boolean!
  $stagingAreaLocationType: String
  $unknownLocationType: String
) {
  importFacilityData(
    facilityId: $facilityId
    imports: $imports
    purgeExistingData: $purgeExistingData
    stagingAreaLocationType: $stagingAreaLocationType
    unknownLocationType: $unknownLocationType
  ) {
    success
    message
    importedRecords {
      fileType
      recordsProcessed
      errors
    }
  }
}

# Where ImportDataInput would be:
input ImportDataInput {
  fileType: String!
  fieldMappings: String!  # JSON-encoded string
  dataStartLine: Int!
  fileName: String!
  csvContent: String!
  trailerNameFormat: String  # Only for trailers
  dateFormat: String  # Only for trailers - format for date fields
  loadStatusMappings: String  # Only for trailers - JSON-encoded mapping of CSV status values to system status values
}
            `);

            // Make the actual GraphQL request
            try {
                const response = await fetch(GRAPHQL_API_URL, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    },
                    body: JSON.stringify({
                        query: importDataMutation,
                        variables: {
                            facilityId,
                            imports,
                            purgeExistingData,
                            stagingAreaLocationType,
                            unknownLocationType
                        }
                    })
                });

                const result = await response.json();

                if (result.errors) {
                    // Handle GraphQL errors (like missing mutation)
                    const error = result.errors[0];
                    if (error.message.includes('Cannot query field') || error.message.includes('Unknown type') || error.message.includes('importFacilityData')) {
                        // Backend mutation doesn't exist yet - show helpful message
                        let devMessage = `🚧 Backend mutation not implemented yet!\n\n`;
                        devMessage += `The import data is ready and was sent to the backend.\n`;
                        devMessage += `Check the Network tab and Console for the complete request.\n\n`;
                        devMessage += `Backend needs to implement:\n`;
                        devMessage += `• ImportDataInput type\n`;
                        devMessage += `• importFacilityData mutation\n`;
                        devMessage += `• importJobStatus query\n\n`;
                        devMessage += `Data summary:\n`;
                        imports.forEach((imp) => {
                            const lines = imp.csvContent.split('\n').length - 1;
                            devMessage += `• ${imp.fileType}: ${imp.fileName} (${Math.max(0, lines - imp.dataStartLine)} rows)\n`;
                        });
                        
                        alert(devMessage);
                    } else {
                        throw new Error(error.message);
                    }
                } else if (result.data?.importFacilityData?.success) {
                    // Job started successfully - get job ID and start tracking
                    const jobId = result.data.importFacilityData.job_id;
                    const message = result.data.importFacilityData.message;
                    
                    if (jobId) {
                        // Set up job tracking
                        setCurrentJobId(jobId);
                        setJobStatus({ 
                            job_id: jobId, 
                            status: 'running', 
                            progress: 0,
                            message: message || 'Import job started'
                        });
                        
                        // Close import modal and show progress modal
                        setShowImportModal(false);
                        setShowProgressModal(true);
                        
                        // Start polling for job status
                        startJobPolling(jobId);
                        
                        console.log(`Import job started with ID: ${jobId}`);
                    } else {
                        throw new Error('No job ID returned from import');
                    }
                } else {
                    throw new Error(result.data?.importFacilityData?.message || 'Import failed');
                }
            } catch (networkError) {
                // Handle network errors
                console.error('Network error during import:', networkError);
                const errorMessage = networkError instanceof Error ? networkError.message : 'Unknown network error occurred';
                alert(`Network error: ${errorMessage}\n\nCheck the console for the complete request structure that was attempted.`);
                throw networkError;
            }
        } catch (error) {
            console.error('Error importing data:', error);
            
            // Show detailed error message
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            alert(`Failed to import data: ${errorMessage}\n\nNote: This mutation may not be implemented yet. Check console for request details.`);
        } finally {
            setImporting(false);
        }
    };

    //   const handleUpdateModalClose = () => {
    //     setShowUpdateModal(false);
    //   };

    const gates = [
        {
            value: 1,
            label: 'Aurora #1'
        },
        {
            value: 2,
            label: 'Aurora #2'
        }
    ];

    // Add the missing handleLayoutSelection function
    const handleLayoutSelection = (layoutId: string) => {
        setSelectedLayout(layoutId);
        const layout = yardLayouts.find(l => l.uuid === layoutId);
        if (layout) {
            setEditedYardLayout(layout);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center mt-4 w-full">
                <CircularProgress/>
                <p className="mt-2">Loading...</p>
            </div>
        );
    }

    if (!facility) {
        return <div>Facility not found</div>;
    }

    const workflow = (name: string, steps: {  label: string; type: string, fork:{forkLabel:string, forkColor:string, forkBackgroundColor:string}[] }[]) => {

        const title = (<h3>{name}</h3>)

        const node_steps = [];

        for (let i = 0; i < steps.length; i++) {
            let node;
            let backgroundColor = '#fff'
            let color = '#000'
            if (i===0){
                backgroundColor ='#000';
                color = '#fff';
            }else if(i===steps.length-1){
                // backgroundColor = '#006400';
                // color = '#fff'
            }

            switch (steps[i].type) {
                case 'entry':
                    backgroundColor = '#fff';
                    color = '#000'
                    break;
                // case 'verification_component':
                //     backgroundColor = 'lightblue';
                //     color = 'black'
                //     break;
                // case 'driver_auth_component':
                //     backgroundColor = 'green';
                //     color = 'white'
                //     break;
                // case 'form':
                //     backgroundColor = 'seagreen';
                //     color = 'white'
                //     break;
                // case 'logic':
                //     backgroundColor = 'black';
                //     color = 'white'
                //     break;
                 case 'black':
                  backgroundColor = 'black';
                     color = 'white'
                     break;
                 case 'gray':
                     backgroundColor = 'gray';
                     color = 'white'
                     break;
                default:
                    backgroundColor = 'darkgreen';
                    color = 'white'
                    break;
            }
            if (steps[i].fork.length > 0) {
                let fork_nodes = []


                for (let j = 0; j < steps[i].fork.length; j++) {
                    let forkLabelElement: JSX.Element;
                    const forkLabel = steps[i].fork[j].forkLabel;
                    if (forkLabel.includes("@")) {
                        const [labelPart, listPart] = forkLabel.split("@");
                        let items = listPart.split("~");
                        // kill first item
                        items.reverse().pop()
                        items.reverse()
                        forkLabelElement = (
                            <>
                                <span>{labelPart}</span>
                                <List sx={{
                                    listStyleType: 'disc',
                                    pl: 4,
                                    '& .MuiListItem-root': {
                                        display: 'list-item'
                                    }
                                }}>
                                    {items.map((item:any
                                        , index:any) => (
                                        <ListItem key={index}>
                                            <ListItemText primary={item} />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        );
                    } else {
                        forkLabelElement = <span>{forkLabel}</span>;
                    }


                    fork_nodes.push(<div style={{width: '2px', height: '20px', background: '#666', margin: '4px 0'}}/>)
                    fork_nodes.push(
                        <div
                            style={{
                                        backgroundColor: steps[i].fork[j].forkBackgroundColor,
                                        border: '1px solid ' + steps[i].fork[j].forkColor,
                                        padding: '8px',
                                        borderRadius: '4px',
                                        color: steps[i].fork[j].forkColor
                                    }}
                                >
                                    {forkLabelElement}
                                </div>
                            )

            }
                node = (<div
                        style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                        <div
                            style={{
                                backgroundColor: backgroundColor,
                                color: color,
                                border: '1px solid #888',
                                padding: '8px',
                                borderRadius: '4px',
                                fontFamily: 'Arial, sans-serif',
                            }}
                        >
                            {steps[i].label}
                        </div>{fork_nodes}</div>)

            }else {
                node = <div
                    style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <div
                        style={{
                            backgroundColor: backgroundColor,
                            color: color,
                            border: '1px solid #888',
                            padding: '8px',
                            borderRadius: '4px',
                            fontFamily: 'Arial, sans-serif',
                        }}
                    >
                        {steps[i].label}
                    </div></div>
                    }
                    node_steps.push(node);
                    // if last step don't add spacer
                    if (i < steps.length - 1) {
                node_steps.push(<div style={{width: '40px', height: '2px', background: '#666', margin: '2% 8px'}}/>)
            }
        }


        const template = (<div>
            {title}
            <div style={{
                display: 'flex',
                border: '1px solid #888',
                padding: '12px',
                paddingBottom: '62px'
            }}>
                {node_steps}
                <span
                    className="text-sky-600 float-right hover:underline cursor-pointer"
                    style={{padding: '50px'}}
                >
      Edit
    </span>
            </div>
        </div>)
        return template;

    }

    return (
        <div className="p-5">
            <div className="shadow bg-[#FBFBFB] p-3">
                <div className="flex">
                    <h1 className="font-bold text-xl mb-3">Facility Info:</h1>
                    <div className="ml-auto flex items-center gap-3">
                        {userIsAdmin && (
                            <button
                                className={twMerge(
                                    'flex items-center gap-1 px-3 py-1 rounded text-sm',
                                    purgingCache 
                                        ? 'bg-gray-300 cursor-not-allowed text-gray-600'
                                        : 'bg-red-100 hover:bg-red-200 text-red-700 border border-red-300'
                                )}
                                onClick={handlePurgeCache}
                                disabled={purgingCache}
                                title="Clear facility cache (Super Admin only)"
                            >
                                <ClearAll fontSize="small" />
                                {purgingCache ? 'Purging...' : 'Purge Cache'}
                            </button>
                        )}
                        {userIsAdmin && (
                            <button
                                className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded text-sm"
                                onClick={() => {
                                    // Reset all import state when opening modal
                                    resetImportState();
                                    setShowImportModal(true);
                                }}
                            >
                                Import Data
                            </button>
                        )}
                        <span 
                            className="text-sky-600 hover:underline cursor-pointer"
                            onClick={handleFacilityEditOpen}
                        >
                            Edit
                        </span>
                    </div>
                </div>
                <p>
                    <span className="font-bold">Name: </span>
                    {facility?.name}
                </p>
                <p>
                    <span className="font-bold">ID: </span>
                    {facility?.id}
                </p>
                <p>
                    <span className="font-bold">Address: </span>
                    {facility?.address}
                </p>
            </div>

            {userIsAdmin && (
                <div className="shadow bg-[#FBFBFB] p-3 mt-5">
                    <h1 className="font-bold text-lg mb-3">Facility Settings</h1>
                    {facility?.activation?.yms ? (
                        <div className="flex items-center gap-2">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={emptyDropEnabled}
                                        onChange={(e) => handleToggleEmptyDrop(e.target.checked)}
                                        disabled={togglingEmptyDrop}
                                        color="primary"
                                    />
                                }
                                label={
                                    <span className="text-sm">
                                        Empty Drop Button {emptyDropEnabled ? '(Enabled)' : '(Disabled)'}
                                    </span>
                                }
                            />
                            {togglingEmptyDrop && <CircularProgress size={16} />}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Empty Drop toggle is not available. YMS must be enabled for this facility.
                        </p>
                    )}
                    {emptyDropError && (
                        <p className="text-red-500 text-sm mt-1">{emptyDropError}</p>
                    )}
                    {facility?.activation ? (
                        <div className="flex items-center gap-2">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={workflowKiosksActive}
                                        onChange={(e) => handleToggleWorkflowKiosks(e.target.checked)}
                                        disabled={togglingWorkflowKiosks}
                                        color="primary"
                                    />
                                }
                                label={
                                    <span className="text-sm">
                                        Kiosks Enabled {workflowKiosksActive ? '(Enabled)' : '(Disabled)'}
                                    </span>
                                }
                            />
                            {togglingWorkflowKiosks && <CircularProgress size={16} />}
                        </div>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Kiosks toggle is not available. Facility must be activated first.
                        </p>
                    )}
                    {workflowKiosksError && (
                        <p className="text-red-500 text-sm mt-1">{workflowKiosksError}</p>
                    )}
                </div>
            )}

            <br/>




            <div className="mb-5">  
                <h1 className="font-bold text-lg mt-5 mb-3">Yard Management</h1>
                {loadingYardLayout ? (
                    <div className="flex flex-col items-center mt-4">
                        <CircularProgress />
                        <p className="mt-2">Loading yard data...</p>
                    </div>
                ) : error && error.includes('Facility zones cannot be managed') ? (
                    <div className="shadow bg-red-50 border border-red-200 p-3">
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : yardLayouts.length > 0 && selectedLayout ? (
                    <YardManagement 
                        yardLayouts={yardLayouts}
                        selectedLayoutId={selectedLayout}
                        onLayoutChange={handleLayoutSelection}
                        onCancel={handleModalCancel}
                    />
                ) : (
                    <div className="shadow bg-[#FBFBFB] p-3">
                        <p className="text-gray-500">No yard data available</p>
                    </div>
                )}
            </div>
            <div className="mb-5">
                <h1 className="font-bold text-lg mt-5 mb-3">Warehouse Management</h1>
                {loadingWarehouse ? (
                    <div className="flex flex-col items-center mt-4">
                        <CircularProgress />
                        <p className="mt-2">Loading warehouse data...</p>
                    </div>
                ) : warehouse ? (
                    <WarehouseManagement 
                        warehouse={warehouse}
                        facility={facility}
                        onCancel={handleModalCancel}
                    />
                ) : (
                    <div className="shadow bg-[#FBFBFB] p-3">
                        <p className="text-gray-500">No warehouse data available</p>
                    </div>
                )}
            </div>
            {/* <div className="mb-5">
                <h1 className="font-bold text-lg mt-5 mb-3">Workflows</h1>
                <div className="">
                    <button
                        className="bg-sky-600 hover:bg-sky-500 text-white p-2 h-[38px] rounded"
                        onClick={() => {

                        }
                    }
                    >
                        Add Workflow
                    </button>

                    <button
            className="bg-sky-600 hover:bg-sky-500 text-white p-2 h-[38px] rounded"
            onClick={() => setShowUpdateModal(true)}
          >
            Update Facility
          </button>
                </div>
            </div> */}


            {/* {workflow('checkin_base_aurora123', [
                    {label: 'QR Code Scan / Kiosk', type: 'entry', fork: []},
                    {
                        label: 'Hours of Operation Rules Component', type: 'fork', fork: [{
                            forkLabel: 'After Hours Text:\n' +
                                '“We are closed. Please return later.”', forkColor: '#000', forkBackgroundColor: '#fff'
                        }]
                    },

                    {
                        label: 'Driver Auth Component', type: 'driver_auth_component', fork: [{
                            forkLabel: 'FR Auth Rules: @\n' +
                                '~Skip if user recognized', forkColor: '#000', forkBackgroundColor: '#fff'
                        },
                            {forkLabel: 'Error - Retry', forkColor: '#f00', forkBackgroundColor: '#fff'}
                        ]
                    },

                    {
                        label: 'Reference # Verification Component', type: 'verification_component', fork: [{
                            forkLabel: 'FR Auth Rules:@ \n' +
                                '~Skip if pre-checked in', forkColor: '#000', forkBackgroundColor: '#fff'
                        },
                            {
                                forkLabel: 'Input(s): @\n' +
                                    '~BOL # (validation_– Appt Integration)\n' +
                                    '~Non-reference # exception (validation – NA)',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Text Box Entry Hint(s): \n' +
                                    '“208"', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Page Text:\n' +
                                    '“Enter Load #: Please enter your load number below. Possible Load # Format(s)…"',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {forkLabel: 'Error - Retry', forkColor: '#f00', forkBackgroundColor: '#fff'},
                        ]
                    },

                    {
                        label: 'Shipment Info Verification Component', type: 'verification_component', fork: [{
                            forkLabel: 'Input(s): @\n' +
                                '~full_name (validation_ field – NA)\n' +
                                '~driver_phone (validation – NA)\n' +
                                '~truck_number (validation – machine vision)\n' +
                                '~trailer_number (validation – machine vision)\n' +
                                '~drop_n_hook (validation – NA)', forkColor: '#000', forkBackgroundColor: '#fff'
                        },
                            {
                                forkLabel: 'Text Box Entry Hint(s): \n' +
                                    'NA', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Page Text:\n' +
                                    '“Confirmation\n' +
                                    'Please confirm the information below is correct. If not, please adjust."',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                        ]
                    },


                    {
                        label: 'Form Acknowledgement Component', type: 'form', fork: [{
                            forkLabel: 'Text: \n' +
                                '“Safety First- Yard/Warehouse\n' +
                                'In inclement weather, ICE CLEATS may be required at some facilities\n' +
                                'Obey all posted plant specific rules and policies\n' +
                                '….”', forkColor: '#000', forkBackgroundColor: '#fff'
                        },

                        ]
                    },

                    {
                        label: 'Shipment Scenario Logic Workflow Fork', type: 'black', fork: [{
                            forkLabel: 'Direction: @\n' +
                                '~Inbound\n' +
                                '~Outbound\n' +
                                '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                        },
                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Exception: @\n' +
                                    '~Dropping Empty (no pickup/ delivery)\n' +
                                    '~Shuttle\n' +
                                    '~Other Visitor', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'check-in_\n' +
                                    'outbound_base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },
                            {
                                forkLabel: 'check-in_\n' +
                                    'inbound_base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },
                            {
                                forkLabel: 'check-in_\n' +
                                    'exception', forkColor: '#fff', forkBackgroundColor: '#000'
                            },
                        ]
                    },

                ]
            )}


            {workflow('checkin_outbound_base_aurora123', [
                    {
                        label: 'checkin_\n' +
                            'base', type: 'black', fork: []
                    },
                    {
                        label: 'Driver\'s License Image Validation Component',
                        type: 'fork',
                        fork: [{
                            forkLabel: 'Error – Indication on FR Web Admin',
                            forkColor: '#f00',
                            forkBackgroundColor: '#fff'
                        }]
                    },

                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'logic', fork: [

                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live\n' +
                                    '~Drop\n' +
                                    '~Drop & Hook\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Ship From/To: @\n' +
                                    '~Internal\n' +
                                    '~Customer\n' +
                                    '~Supplier\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {forkLabel: 'Success / Error', forkColor: '#000', forkBackgroundColor: '#fff'},

                        ]
                    },

                    {
                        label: 'Success / Error Screen / Text Message', type: 'logic', fork: [
                            {
                                forkLabel: 'Static Text: \n' +
                                    '“Check-In Complete for Shipment: XXXXXXX… ',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}

            {workflow('checkin_inbound_base_aurora123', [
                    {
                        label: 'checkin_\n' +
                            'base', type: 'black', fork: []
                    },
                    {
                        label: 'Driver\'s License Image Validation Component',
                        type: 'fork',
                        fork: [{
                            forkLabel: 'Error – Indication on FR Web Admin',
                            forkColor: '#f00',
                            forkBackgroundColor: '#fff'
                        }]
                    },

                    {
                        label: 'Chain of Custody Image Validation Component', type: 'custody_component', fork: [

                            {
                                forkLabel: 'Image Capture: \n' +
                                    'Seal # on BOL', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Data Capture: \n' +
                                    'Seal # from BOL', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Validation: \n' +
                                    'NA', forkColor: '#000', forkBackgroundColor: '#fff'
                            },


                        ]
                    },


                    {
                        label: 'Chain of Custody Image Validation Component', type: 'custody_component', fork: [

                            {
                                forkLabel: 'Image Capture: \n' +
                                    'Physical Seal', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Data Capture: \n' +
                                    'Seal # from physical seal', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Validation: \n' +
                                    'Matches seal on BOL', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Error – Indication on FR Web Admin',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            },


                        ]
                    },

                    {
                        label: 'BOL Document  Capture Component', type: 'bol_capture_component', fork: [

                            {
                                forkLabel: 'Upload physical document via kiosk / DC',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},


                        ]
                    },

                    {
                        label: 'Shipment Scenario Logic Workflow Fork', type: 'logic', fork: [
                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'check_in_\n' +
                                    'inbound_reefer', forkColor: '#fff', forkBackgroundColor: '#000'
                            },


                        ]
                    },

                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'gray', fork: [
                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live, \n' +
                                    '~Drop, \n' +
                                    '~Drop & Hook,\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: ' Ship From/To:@\n' +
                                    '                ~Internal\n' +
                                    '                ~Customer\n' +
                                    '                ~Supplier\n' +
                                    '                ~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },


                        ]
                    },

                    {
                        label: 'Success Screen / Text Message', type: 'black', fork: [
                            {
                                forkLabel: 'Static Text: \n' +
                                    '“Check-In Complete for Shipment: XXXXXXX… ',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}


            {workflow('checkin_inbound_reefer_aurora123\n', [
                    {
                        label: 'checkin_inbound_\n' +
                            'base', type: 'black', fork: []
                    },

                    {
                        label: 'Chain of Custody Image Validation Component', type: 'custody_component', fork: [

                            {
                                forkLabel: 'Image Capture: \n' +
                                    'Reefer temp', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Data Capture: \n' +
                                    'Reefer temp', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Validation: \n' +
                                    'Facility defined tolerance', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Error – Indication on FR Web Admin',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            },

                        ]
                    },


                    {
                        label: 'Chain of Custody Image Validation Component', type: 'custody_component', fork: [

                            {
                                forkLabel: 'Image Capture: \n' +
                                    'Reefer fuel gauge', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Data Capture: \n' +
                                    'Reefer fuel gauge %', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Validation: \n' +
                                    'Facility defined tolerance', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Error – Indication on FR Web Admin',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            },


                        ]
                    },


                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'logic', fork: [
                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live, \n' +
                                    '~Drop, \n' +
                                    '~Drop & Hook,\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: ' Ship From/To:\n@' +
                                    '                ~Internal\n' +
                                    '                ~Customer\n' +
                                    '                ~Supplier\n' +
                                    '                ~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },


                        ]
                    },

                    {
                        label: 'Success Screen / Text Message', type: 'logic', fork: [
                            {
                                forkLabel: 'Static Text: \n' +
                                    '“Check-In Complete for Shipment: XXXXXXX… ',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}


            {workflow('checkin_exception_aurora123\n', [
                    {
                        label: 'checkin_inbound_\n' +
                            'base', type: 'black', fork: []
                    },


                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'gray', fork: [
                            {
                                forkLabel: 'Exception: @\n' +
                                    '~Dropping Empty (no pickup/ delivery)\n' +
                                    '~Shuttle\n' +
                                    '~Other Visitor', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {forkLabel: 'Success / Error', forkColor: '#000', forkBackgroundColor: '#fff'},


                        ]
                    },

                    {
                        label: 'Success Screen / Text Message', type: 'logic', fork: [
                            {
                                forkLabel: 'Static Text: \n' +
                                    '“Check-In Complete for Shipment: XXXXXXX… ',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}


            {workflow('chainofcustody_outbound_aurora123', [
                    {label: 'Kiosk / (BOL Sign Link)', type: 'entry', fork: []},


                    {
                        label: 'Reference # Verification Component', type: 'verification_component', fork: [{
                            forkLabel: 'Input(s): @\n' +
                                '~Seal # (validation_– BOL Integration)\n' +
                                '~Skip if user + shipment recognized on their device within 3 hours of check-in',
                            forkColor: '#000',
                            forkBackgroundColor: '#fff'
                        },
                            {
                                forkLabel: 'Text Box Entry Hint(s): \n' +
                                    'NA', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Page Text:\n' +
                                    '“Enter Seal Number\n' +
                                    'Please enter your seal number below."', forkColor: '#000', forkBackgroundColor: '#fff'
                            },


                            {forkLabel: 'Error - Retry', forkColor: '#f00', forkBackgroundColor: '#fff'},
                        ]
                    },

                    {
                        label: 'Driver Auth Component', type: 'driver_auth_component', fork: [{
                            forkLabel: 'FR Auth Rules:@ \n' +
                                '~Skip if pre-checked in within the last 3 hours on kiosk\n' +
                                '~Skip if user + shipment recognized on their device within 3 hours of check-in',
                            forkColor: '#000',
                            forkBackgroundColor: '#fff'
                        },
                            {forkLabel: 'Error - Retry', forkColor: '#f00', forkBackgroundColor: '#fff'}
                        ]
                    },


                    {
                        label: 'Shipment Info Verification Component', type: 'verification_component', fork: [{
                            forkLabel: 'Input(s): \n@' +
                                '~full_name (validation_ field – NA…prefill)\n' +
                                '~truck_number (validation – NA …prefill)', forkColor: '#000', forkBackgroundColor: '#fff'
                        },
                            {
                                forkLabel: 'Text Box Entry Hint(s): \n' +
                                    'NA', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Page Text:\n' +
                                    '“Confirmation\n' +
                                    'Please confirm the information below is correct. If not, please adjust."',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                        ]
                    },


                    {
                        label: 'Chain of Custody Capture Component', type: 'custody_component', fork: [

                            {
                                forkLabel: 'Shipment Text Display: @\n' +
                                    '~Weight\n' +
                                    '~Ship To\n' +
                                    '~Trailer #\n' +
                                    '~Seal #', forkColor: '#000', forkBackgroundColor: '#fff'
                            },


                            {
                                forkLabel: 'Error – "Please see DC…" – Manual Override',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            }


                        ]
                    },

                    {
                        label: 'Outbound BOL Print Component', type: 'bol_print_component', fork: [

                            {
                                forkLabel: 'Inputs: \n@' +
                                    '~Printer IP Address (initialized on kiosk)\n' +
                                    '~# of copies to be printed (pre-configured or dynamic based on ship to / carrier)',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },


                            {
                                forkLabel: 'Error – "Please see DC…" – Manual Override',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            }


                        ]
                    },

                    {
                        label: 'Success Screen', type: 'logic', fork: [

                            {
                                forkLabel: 'Static Text: \n' +
                                    '“You are successfully checked out!\n' +
                                    '\n' +
                                    'You will receive a text with a link to your digital BOL.…"',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {
                                forkLabel: 'Success confirmation link to BOL / Gate Exit Information:\n@' +
                                    '~QR Code / PIN\n' +
                                    '~Link to BOL ', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                        ]
                    },

                ]
            )}


            {workflow('chainofcustody_intbound_aurora123', [
                    {label: 'FR Web Admin', type: 'entry', fork: []},


                    {
                        label: 'FR Admin Signature Capture Component', type: 'admin_component', fork: [{
                            forkLabel: 'Input(s): @\n' +
                                '~OS&D (validation_– NA)\n' +
                                '~Subject to Count (validation – NA)', forkColor: '#000', forkBackgroundColor: '#fff'
                        },
                        ]
                    },


                    {
                        label: 'Success Screen', type: 'logic', fork: [

                            {
                                forkLabel: 'Static Text: \n' +
                                    '“You are successfully checked out!\n' +
                                    '\n' +
                                    'You will receive a text with a link to your digital BOL.…"',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {
                                forkLabel: 'Success confirmation link to BOL / Gate Exit Information:\n' +
                                    'QR Code / PIN\n' +
                                    'Link to BOL ', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                        ]
                    },

                ]
            )}


            {workflow('checkout_base_aurora123', [
                    {label: 'QR Code Scan / Kiosk', type: 'entry', fork: []},


                    {
                        label: 'Driver Auth Component', type: 'driver_auth_component', fork: [
                            {
                                forkLabel: 'FR Auth Rules: @\n' +
                                    '~Skip if user recognized', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {forkLabel: 'Error - Retry', forkColor: '#f00', forkBackgroundColor: '#fff'}
                        ]
                    },

                    {
                        label: 'Reference # Verification Component', type: 'verification_component', fork: [

                            {
                                forkLabel: 'FR Auth Rules: @\n' +
                                    '~Skip if checked in', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Input(s): @\n' +
                                    '~BOL # (validation_– Appt Integration)\n' +
                                    '~Non-reference # exception (validation – NA)',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Text Box Entry Hint(s): \n' +
                                    '"208"', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Page Text:\n' +
                                    '“Enter Load #: Please enter your load number below. Possible Load # Format(s)…"',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {forkLabel: 'Error - Retry', forkColor: '#f00', forkBackgroundColor: '#fff'},
                        ]
                    },

                    {
                        label: 'Shipment Info Verification Component', type: 'verification_component', fork: [
                            {
                                forkLabel: 'FR Auth Rules: @\n' +
                                    '~Skip if info already known', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Input(s): @\n' +
                                    '~full_name (validation_ field – NA)\n' +
                                    '~driver_phone (validation – NA)\n' +
                                    '~truck_number (validation – machine vision)\n' +
                                    '~trailer_number (validation – machine vision)',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Text Box Entry Hint(s): \n' +
                                    'NA', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Page Text:\n' +
                                    '“Confirmation\n' +
                                    'Please confirm the information below is correct. If not, please adjust."',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                        ]
                    },


                    {
                        label: 'Shipment Scenario Logic Workflow Fork', type: 'black', fork: [
                            {
                                forkLabel: 'Trailer Status: @\n' +
                                    '~Empty\n' +
                                    '~Loaded\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Exception: @\n' +
                                    '~Dropping Empty (no pickup/ delivery)\n' +
                                    '~Shuttle\n' +
                                    '~Other Visitor', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {forkLabel: 'check_out_loaded_base', forkColor: '#fff', forkBackgroundColor: '#000'},
                            {forkLabel: 'check_out_empty_base', forkColor: '#fff', forkBackgroundColor: '#000'},
                            {
                                forkLabel: 'check-out_\n' +
                                    'exception', forkColor: '#fff', forkBackgroundColor: '#000'
                            },
                        ]
                    },

                ]
            )}

            {workflow('checkout_loaded_base_aurora123\n', [
                    {
                        label: 'checkout_\n' +
                            'base', type: 'black', fork: []
                    },

                    {
                        label: 'Shipment Scenario Logic Workflow Fork', type: 'logic', fork: [

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'check-out_\n' +
                                    'loaded_reefer', forkColor: 'white', forkBackgroundColor: 'black'
                            },


                        ]
                    },


                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'logic', fork: [
                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live \n' +
                                    '~Drop \n' +
                                    '~Drop & Hook\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: ' Ship From/To:\n@' +
                                    '                ~Internal\n' +
                                    '                ~Customer\n' +
                                    '                ~Supplier\n' +
                                    '                ~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {forkLabel: 'Success / Error', forkColor: '#000', forkBackgroundColor: '#fff'},
                        ]
                    },

                    {
                        label: 'Success Screen / Text Message', type: 'black', fork: [
                            {
                                forkLabel: 'Static Text: \n' +
                                    '“Check-Out Complete for Shipment: XXXXXXX…',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}

            {workflow('checkout_loaded_reefer_aurora123', [
                    {
                        label: 'checkout_loaded_\n' +
                            'base', type: 'black', fork: []
                    },

                    {
                        label: 'Chain of Custody Image Validation Component', type: 'logic', fork: [

                            {
                                forkLabel: 'Image Capture: \n' +
                                    'Reefer temp', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Data Capture: \n' +
                                    'Reefer temp', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Validation: \n' +
                                    'Facility defined tolerance', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Error – Indication on FR Web Admin',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            },

                        ]
                    },


                    {
                        label: 'Chain of Custody Image Validation Component', type: 'logic', fork: [

                            {
                                forkLabel: 'Image Capture: \n' +
                                    'Reefer fuel gauge', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Data Capture: \n' +
                                    'Reefer fuel gauge %', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Validation: \n' +
                                    'Facility defined tolerance', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Error – Indication on FR Web Admin',
                                forkColor: '#f00',
                                forkBackgroundColor: '#fff'
                            },


                        ]
                    },


                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'gray', fork: [
                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live, \n' +
                                    '~Drop, \n' +
                                    '~Drop & Hook,\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: ' Ship From/To:\n@' +
                                    '                ~Internal\n' +
                                    '                ~Customer\n' +
                                    '                ~Supplier\n' +
                                    '                ~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },


                        ]
                    },

                    {
                        label: 'Success Screen / Text Message', type: 'logic', fork: [
                            {
                                forkLabel: '“Check-Out Complete for Shipment: XXXXXXX… ',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}


            {workflow('checkout_empty_base_aurora123\n', [
                    {
                        label: 'checkout_\n' +
                            'base', type: 'black', fork: []
                    },


                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'gray', fork: [
                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live, \n' +
                                    '~Drop, \n' +
                                    '~Drop & Hook,\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: ' Ship From/To:@\n' +
                                    '                ~Internal\n' +
                                    '                ~Customer\n' +
                                    '                ~Supplier\n' +
                                    '                ~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                        ]
                    },

                    {
                        label: 'Success Screen / Text Message', type: 'logic', fork: [
                            {
                                forkLabel: 'Static Text: \n' +
                                    '“Check-Out Complete for Shipment: XXXXXXX…',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): \n' +
                                    '“You will receive information about where to… ”',
                                forkColor: 'white',
                                forkBackgroundColor: 'gray'
                            },

                            {
                                forkLabel: 'Gate Entry Info:\n' +
                                    'QR Code / PIN', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'driver_\n' +
                                    'orchestration_\n' +
                                    'base', forkColor: '#fff', forkBackgroundColor: '#000'
                            },

                            {forkLabel: 'Error – "Please see DC…"', forkColor: '#f00', forkBackgroundColor: '#fff'},

                        ]
                    },


                ]
            )}


            {workflow('driverorchestration_base_aurora123\n', [
                    {label: 'message_trigger', type: 'black', fork: []},


                    {
                        label: 'Shipment Scenario Logic Message Fork', type: 'gray', fork: [
                            {
                                forkLabel: 'Direction: @\n' +
                                    '~Inbound\n' +
                                    '~Outbound\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {
                                forkLabel: 'Scenario: @\n' +
                                    '~Live, \n' +
                                    '~Drop, \n' +
                                    '~Drop & Hook,\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: 'Trailer Type: @\n' +
                                    '~Dry Van\n' +
                                    '~Reefer\n' +
                                    '~Flatbed\n' +
                                    '~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },

                            {
                                forkLabel: ' Ship From/To:@\n' +
                                    '                ~Internal\n' +
                                    '                ~Customer\n' +
                                    '                ~Supplier\n' +
                                    '                ~Other', forkColor: '#000', forkBackgroundColor: '#fff'
                            },
                            {forkLabel: 'Success / Error', forkColor: '#000', forkBackgroundColor: '#fff'},
                        ]
                    },

                    {
                        label: 'Driver Text Message', type: 'logic', fork: [
                            {
                                forkLabel: 'Dynamic Text (based on shipment scenario): @\n' +
                                    '~Check-in Success:  "You\\\'ve successfully checked in for shipment: XXXXXXX"\n' +
                                    '~Chain of custody signature success:  "View Bill of Lading: ..."\n' +
                                    '~BOL available:  "Please proceed to kiosk to sign BOL#: XXXXXXX"\n' +
                                    '~Job assignment available:\n' +
                                    '~Drop:  "Please drop your trailer in spot XX."\n' +
                                    '~Live Pickup: "Please back into dock XX."\n' +
                                    '~Live Delivery: "Please back into dock XX."\n' +
                                    '~Hook: "Please pick up your trailer in spot XX."\n' +
                                    '~Drop & Hook: "Please drop your trailer in spot XX and pick up your loaded trailer in spot YY."',
                                forkColor: '#000',
                                forkBackgroundColor: '#fff'
                            },

                        ]
                    },


                ]
            )} */}


            {/*{workflow('aurora_checkin_fork',[*/}
            {/*    {label:'Authorize Driver', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Shipment Input (shipment_number)', type:'fork', forkLabel:'Not Found', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}
            {/*    {label:'Shipment Type (inbound/outbound)', type:'fork', forkLabel:'inbound: aurora_checkin_fork_inbound', forkColor: 'white', forkBackgroundColor:'black'},*/}

            {/*    {label:'Driver Input', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Validate', type:'fork', forkLabel:'Error', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}
            {/*    {label:'Success', type: 'fork', forkLabel:'Driver Check-In SMS', forkColor:'white', forkBackgroundColor:'#006400'},*/}

            {/*    ]*/}

            {/*)}*/}

            {/*{workflow('aurora_checkin_fork_inbound',[*/}
            {/*    {label:'Driver Input', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Scan BOL', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Seal Input', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Seal Imaging', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'BOL Seal Imaging', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Shipment Type (reefer/none)', type:'fork', forkLabel:'reefer: aurora_checkin_fork_inbound', forkColor: 'white', forkBackgroundColor:'black'},*/}

            {/*        {label:'Validate', type:'fork', forkLabel:'Error', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}
            {/*    {label:'Success', type: 'fork', forkLabel:'Driver Check-In SMS', forkColor:'white', forkBackgroundColor:'#006400'},*/}

            {/*    ]*/}

            {/*)}*/}

            {/*{workflow('aurora_checkin_fork_inbound_reefer',[*/}
            {/*    {label:'Fuel Gauge Imaging', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Temp Imaging', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*    {label:'Temp Set Point Imaging', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}

            {/*        {label:'Validate', type:'fork', forkLabel:'Error', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}
            {/*    {label:'Success', type: 'fork', forkLabel:'Driver Check-In SMS', forkColor:'white', forkBackgroundColor:'#006400'},*/}

            {/*    ]*/}

            {/*)}*/}

            {/*{workflow('aurora_checkout_fork',[*/}
            {/*        {label:'Shipment Input (seal_number)', type:'fork', forkLabel:'Not Found', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}

            {/*        {label:'Authorize Driver', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*        {label:'Shipment Type (inbound/outbound)', type:'fork', forkLabel:'inbound: aurora_checkout_fork_inbound', forkColor: 'white', forkBackgroundColor:'black'},*/}

            {/*        {label:'Validate', type:'fork', forkLabel:'Error', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}
            {/*        {label:'Success', type: 'default', forkLabel:'Driver Check-In SMS', forkColor:'white', forkBackgroundColor:'#006400'},*/}

            {/*    ]*/}

            {/*)}*/}

            {/*{workflow('aurora_checkout_fork_inbound',[*/}
            {/*        {label:'Driver Input', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}

            {/*        {label:'Adopt Signature', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}
            {/*        {label:'Sign BOL', type: 'default', forkLabel:'', forkColor:'', forkBackgroundColor:''},*/}

            {/*        {label:'Validate', type:'fork', forkLabel:'Error', forkColor: '#f00', forkBackgroundColor:'#fff'},*/}
            {/*        {label:'Success', type: 'default', forkLabel:'Driver Check-Out SMS', forkColor:'white', forkBackgroundColor:'#006400'},*/}

            {/*    ]*/}

            {/*)}*/}


            {/*    <h1 className="font-bold text-lg mt-5 mb-3">Site-Specific Messages</h1>*/}
            {/*    <div className="bg-white p-4 w-full">*/}
            {/*<span className=" text-sky-600 float-right hover:underline cursor-pointer">*/}
            {/*  Edit*/}
            {/*</span>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Load # Format:</span> Please enter your load number*/}
            {/*            below. Possible Load # Format(s): Starts with "2" (9 digits after 2)*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Successful Check-in: </span>You will receive*/}
            {/*            information about where to drop your empty, where to dock your truck and/or*/}
            {/*            where to pick up your loaded trailer via text message*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Check-In with an Issue: </span>Unfortunately there*/}
            {/*            was an issue with your check-in. Once you have entered the gate, please proceed*/}
            {/*            to the dock office and show them this receipt*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Drop: </span>Please drop your trailer in spot XX.*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Live Pickup: </span> Please back into dock XX.*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Live Delivery: </span>Please back into dock XX.*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Hook: </span>Please pick up your trailer in spot XX.*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">Drop & Hook: </span>Please drop your trailer in spot*/}
            {/*            XX and pick up your loaded trailer in spot YY.*/}
            {/*        </p>*/}
            {/*        <p>*/}
            {/*            <span className="font-bold">After Hours Message: </span>We are closed. Please*/}
            {/*            return later.*/}
            {/*        </p>*/}
            {/*    </div>*/}
            {showGateModal && (
                <Modal
                    open={showGateModal}
                    onClose={handleGateModalClose}
                    className="flex justify-center items-center"
                >
                    <div className="w-96 bg-white border p-5 min-h-48 relative flex flex-col">
                        <Close
                            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                            onClick={handleGateModalClose}
                        />
                        {!!showGateSuccess ? (
                            <div className="flex justify-center items-center flex-col mt-5">
                                <h1 className="text-xl text-green-500">{gate} Opened!</h1>
                                <Check className="text-green-500 mt-2" fontSize="large"/>
                            </div>
                        ) : (
                            <>
                                <h1 className="text-xl mb-5">Open Gate</h1>
                                <h1 className="text-xl mb-5">Please select which gate to open:</h1>
                                <Select
                                    options={gates}
                                    placeholder="Gates"
                                    onChange={(selectedOption: any) => {
                                        setGate(selectedOption?.label);
                                    }}
                                />
                                <div className="flex justify-between mt-3">
                                    <button
                                        className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded w-24"
                                        onClick={handleGateModalClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={twMerge(
                                            'bg-sky-600 hover:bg-sky-500 text-white p-2 rounded w-24',
                                            !gate && 'bg-gray-300 hover:bg-gray-300 cursor-not-allowed'
                                        )}
                                        onClick={handleOpenGate}
                                        disabled={!gate}
                                    >
                                        Open
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}
            
            {showPinModal && (
                <Modal
                    open={showPinModal}
                    onClose={handlePinModalClose}
                    className="flex justify-center items-center"
                >
                    <div className="w-96 bg-white border p-5 min-h-48 relative flex flex-col">
                        <Close
                            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                            onClick={handlePinModalClose}
                        />
                        {pin ? (
                            <div className="flex justify-center items-center flex-col">
                                <h1 className="text-xl mb-2">Please use this PIN at {gate}:</h1>
                                <h1 className="text-xl">{pin}</h1>
                                <button
                                    className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded mt-10"
                                    onClick={handlePinModalClose}
                                >
                                    Close
                                </button>
                            </div>
                        ) : (
                            <>
                                <h1 className="text-xl mb-5">Generate PIN</h1>
                                <h1 className="text-xl mb-5">
                                    Please select which gate to provide a PIN for:
                                </h1>
                                <Select
                                    options={gates}
                                    placeholder="Gates"
                                    onChange={(selectedOption: any) => {
                                        setGate(selectedOption?.label);
                                    }}
                                />
                                <div className="flex justify-between mt-3">
                                    <button
                                        className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded w-24"
                                        onClick={handlePinModalClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={twMerge(
                                            'bg-sky-600 hover:bg-sky-500 text-white p-2 rounded w-24',
                                            !gate && 'bg-gray-300 hover:bg-gray-300 cursor-not-allowed'
                                        )}
                                        onClick={handleGeneratePin}
                                        disabled={!gate}
                                    >
                                        Generate
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal>
            )}
            {/* {showUpdateModal && (
        <Modal
          open={showUpdateModal}
          onClose={handleUpdateModalClose}
          className="flex justify-center items-center"
        >
          <div className="w-96 bg-white border p-5 min-h-48 relative flex flex-col">
            <Close
              className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
              onClick={handleUpdateModalClose}
            />
            <h1 className="text-xl mb-5">Update Facility</h1>

            <button
              className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded mt-3"
              onClick={handleUpdateModalClose}
            >
              Cancel
            </button>
            <button
              className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded mt-3"
              onClick={handleUpdateModalClose}
            >
              Update
            </button>
          </div>
        </Modal>
      )} */}

            {/* Facility Edit Modal */}
            {showFacilityEditModal && (
                <Modal
                    open={showFacilityEditModal}
                    onClose={handleFacilityEditClose}
                    className="flex justify-center items-center"
                >
                    <div className="w-[600px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative flex flex-col">
                        <Close
                            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                            onClick={handleFacilityEditClose}
                        />
                        <h1 className="text-xl mb-5 font-bold">Edit Facility</h1>
                        
                        {/* Basic Facility Info */}
                        {/* <div className="mb-6">
                            <h2 className="text-lg font-semibold mb-3">Basic Information</h2>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Name:</label>
                                    <input
                                        type="text"
                                        value={editedFacility?.name || ''}
                                        onChange={(e) => setEditedFacility(prev => prev ? {...prev, name: e.target.value} : null)}
                                        className="w-full p-2 border border-gray-300 rounded"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Address:</label>
                                    <input
                                        type="text"
                                        value={editedFacility?.address || ''}
                                        onChange={(e) => setEditedFacility(prev => prev ? {...prev, address: e.target.value} : null)}
                                        className="w-full p-2 border border-gray-300 rounded"
                                    />
                                </div>
                            </div>
                        </div> */}

                        {/* Allowed SCACs Section */}
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold mb-3">Allowed SCACs</h2>
                            <div className="space-y-3">
                                {/* Add SCAC Input */}
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Enter SCAC code (e.g., UPSS, FDXG)"
                                        value={newScac}
                                        onChange={(e) => setNewScac(e.target.value.toUpperCase())}
                                        onKeyPress={handleScacInputKeyPress}
                                        className="flex-1 p-2 border border-gray-300 rounded uppercase"
                                        maxLength={4}
                                    />
                                    <button
                                        onClick={handleAddScac}
                                        disabled={!newScac.trim()}
                                        className={twMerge(
                                            'px-4 py-2 rounded text-white',
                                            newScac.trim() 
                                                ? 'bg-sky-600 hover:bg-sky-500' 
                                                : 'bg-gray-300 cursor-not-allowed'
                                        )}
                                    >
                                        <Add fontSize="small" />
                                    </button>
                                </div>

                                {/* Display Allowed SCACs */}
                                <div className="min-h-[100px] max-h-[200px] overflow-y-auto border border-gray-200 rounded p-3">
                                    {allowedScacs.length === 0 ? (
                                        <p className="text-gray-500 italic">No SCAC codes added yet</p>
                                    ) : (
                                        <div className="flex flex-wrap gap-2">
                                            {allowedScacs.map((scac, index) => (
                                                <div
                                                    key={index}
                                                    className="flex items-center gap-1 bg-sky-100 text-sky-800 px-3 py-1 rounded-full"
                                                >
                                                    <span className="font-mono font-medium">{scac}</span>
                                                    <button
                                                        onClick={() => handleRemoveScac(scac)}
                                                        className="text-sky-600 hover:text-sky-800"
                                                    >
                                                        <Close fontSize="small" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                
                                <p className="text-sm text-gray-600">
                                    SCAC (Standard Carrier Alpha Code) is a unique code assigned to transportation companies.
                                    Add codes to specify which carriers are allowed at this facility.
                                </p>
                            </div>
                        </div>

                        {/* Timezone Section */}
                        <div className="mb-6">
                            <h2 className="text-lg font-semibold mb-3">Timezone Configuration</h2>
                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Timezone:</label>
                                    <Select
                                        options={[
                                            { value: '', label: 'Select timezone...' },
                                            { value: 'US/Eastern', label: 'US/Eastern (EST/EDT)' },
                                            { value: 'US/Central', label: 'US/Central (CST/CDT)' },
                                            { value: 'US/Mountain', label: 'US/Mountain (MST/MDT)' },
                                            { value: 'US/Pacific', label: 'US/Pacific (PST/PDT)' },
                                            { value: 'US/Alaska', label: 'US/Alaska (AKST/AKDT)' },
                                            { value: 'US/Hawaii', label: 'US/Hawaii (HST)' },
                                            { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
                                            { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
                                            { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
                                            { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
                                            { value: 'America/Phoenix', label: 'America/Phoenix (MST - No DST)' },
                                            { value: 'America/Anchorage', label: 'America/Anchorage (AKST/AKDT)' },
                                            { value: 'America/Honolulu', label: 'America/Honolulu (HST)' },
                                            { value: 'Canada/Atlantic', label: 'Canada/Atlantic (AST/ADT)' },
                                            { value: 'Canada/Eastern', label: 'Canada/Eastern (EST/EDT)' },
                                            { value: 'Canada/Central', label: 'Canada/Central (CST/CDT)' },
                                            { value: 'Canada/Mountain', label: 'Canada/Mountain (MST/MDT)' },
                                            { value: 'Canada/Pacific', label: 'Canada/Pacific (PST/PDT)' },
                                            { value: 'America/Toronto', label: 'America/Toronto (EST/EDT)' },
                                            { value: 'America/Winnipeg', label: 'America/Winnipeg (CST/CDT)' },
                                            { value: 'America/Edmonton', label: 'America/Edmonton (MST/MDT)' },
                                            { value: 'America/Vancouver', label: 'America/Vancouver (PST/PDT)' },
                                            { value: 'America/Halifax', label: 'America/Halifax (AST/ADT)' },
                                            { value: 'America/St_Johns', label: 'America/St_Johns (NST/NDT)' },
                                            { value: 'UTC', label: 'UTC (Coordinated Universal Time)' }
                                        ]}
                                        value={timezone ? { value: timezone, label: timezone } : null}
                                        onChange={(selectedOption: any) => setTimezone(selectedOption?.value || '')}
                                        className="w-full"
                                        isSearchable
                                        isClearable
                                        placeholder="Select timezone..."
                                        isDisabled={!userIsAdmin}
                                    />
                                    <p className="text-sm text-gray-600 mt-1">
                                        Select the timezone for this facility. This affects how appointment times and detention calculations are displayed.
                                        {!userIsAdmin && <span className="text-red-600 font-medium"> (Admin access required)</span>}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-between mt-6">
                            <button
                                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
                                onClick={handleFacilityEditClose}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-sky-600 hover:bg-sky-500 text-white px-6 py-2 rounded"
                                onClick={handleFacilityEditSave}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Import Data Modal */}
            {showImportModal && (
                <Modal
                    open={showImportModal}
                    onClose={handleImportModalClose}
                    className="flex justify-center items-center"
                >
                    <div className="w-[800px] bg-white border p-6 max-h-[90vh] overflow-y-auto relative">
                        <Close
                            className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                            onClick={handleImportModalClose}
                        />
                        
                        {/* Step indicator */}
                        <div className="flex items-center mb-6">
                            <div className={twMerge(
                                'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                                importStep >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                            )}>
                                1
                            </div>
                            <div className={twMerge(
                                'flex-1 h-1 mx-2',
                                importStep >= 2 ? 'bg-blue-600' : 'bg-gray-200'
                            )}></div>
                            <div className={twMerge(
                                'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                                importStep >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                            )}>
                                2
                            </div>
                            <div className={twMerge(
                                'flex-1 h-1 mx-2',
                                importStep >= 3 ? 'bg-blue-600' : 'bg-gray-200'
                            )}></div>
                            <div className={twMerge(
                                'flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium',
                                importStep >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                            )}>
                                3
                            </div>
                        </div>

                        <h1 className="text-xl font-semibold mb-6">
                            {importStep === 1 ? 'Import Data - Upload Files' : 
                             importStep === 2 ? 'Import Data - Configuration' : 
                             'Import Data - Field Mapping'}
                        </h1>

                        {importStep === 1 ? (
                            <div className="space-y-6">
                                {/* General CSV Upload Section */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <h2 className="text-lg font-medium mb-3">Structure Data Import</h2>
                                    <p className="text-gray-600 text-sm mb-3">
                                        Upload a CSV file containing facility structure data (zones, dock doors, yard spots).
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleGeneralCsvChange}
                                                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                            />
                                            {generalCsvFile && (
                                                <span className="text-sm text-green-600">
                                                    ✓ {generalCsvFile.name}
                                                </span>
                                            )}
                                        </div>
                                        {generalCsvFile && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-3">
                                                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                                        Data starts on line:
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={generalDataStartLine}
                                                        onChange={(e) => handleGeneralDataStartLineChange(parseInt(e.target.value) || 1)}
                                                        className="w-20 p-1 border border-gray-300 rounded text-sm text-center"
                                                    />
                                                    <span className="text-xs text-gray-500">
                                                        (Line {generalDataStartLine} will be used as headers)
                                                    </span>
                                                    <button
                                                        onClick={() => handleAutoDetectGeneralHeaders()}
                                                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded border border-blue-300"
                                                        type="button"
                                                    >
                                                        🔍 Auto-detect
                                                    </button>
                                                </div>
                                                {generalCsvHeaders.length > 0 && (
                                                    <div className="text-xs text-gray-600">
                                                        <strong>Headers detected:</strong> {generalCsvHeaders.slice(0, 5).join(', ')}{generalCsvHeaders.length > 5 ? '...' : ''}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Active Trailers CSV Upload Section */}
                                <div className="border border-gray-200 rounded-lg p-4">
                                    <h2 className="text-lg font-medium mb-3">Active Trailers Import</h2>
                                    <p className="text-gray-600 text-sm mb-3">
                                        Upload a CSV file containing active trailer data.
                                    </p>
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="file"
                                                accept=".csv"
                                                onChange={handleTrailersCsvChange}
                                                className="text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                                            />
                                            {trailersCsvFile && (
                                                <span className="text-sm text-green-600">
                                                    ✓ {trailersCsvFile.name}
                                                </span>
                                            )}
                                        </div>
                                        {trailersCsvFile && (
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-3">
                                                    <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                                        Data starts on line:
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={trailersDataStartLine}
                                                        onChange={(e) => handleTrailersDataStartLineChange(parseInt(e.target.value) || 1)}
                                                        className="w-20 p-1 border border-gray-300 rounded text-sm text-center"
                                                    />
                                                    <span className="text-xs text-gray-500">
                                                        (Line {trailersDataStartLine} will be used as headers)
                                                    </span>
                                                    <button
                                                        onClick={() => handleAutoDetectTrailersHeaders()}
                                                        className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs rounded border border-blue-300"
                                                        type="button"
                                                    >
                                                        🔍 Auto-detect
                                                    </button>
                                                </div>
                                                {trailersCsvHeaders.length > 0 && (
                                                    <div className="text-xs text-gray-600">
                                                        <strong>Headers detected:</strong> {trailersCsvHeaders.slice(0, 5).join(', ')}{trailersCsvHeaders.length > 5 ? '...' : ''}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Purge Existing Data Option */}
                                <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                                    <div className="flex items-start gap-3">
                                        <input
                                            type="checkbox"
                                            id="purgeExistingData"
                                            checked={purgeExistingData}
                                            onChange={(e) => setPurgeExistingData(e.target.checked)}
                                            className="mt-1"
                                        />
                                        <div>
                                            <label htmlFor="purgeExistingData" className="font-medium text-orange-900 cursor-pointer">
                                                Purge Existing Data
                                            </label>
                                            <p className="text-sm text-orange-800 mt-1">
                                                ⚠️ <strong>Warning:</strong> This will delete all existing data of the selected types before importing. 
                                                Use with caution as this action cannot be undone.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Information Box */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h3 className="font-medium text-blue-900 mb-2">Upload Instructions</h3>
                                    <ul className="text-sm text-blue-800 space-y-1">
                                        <li>• Upload at least one CSV file to continue</li>
                                        <li>• Files should be in standard CSV format with headers</li>
                                        <li>• The next step will allow you to map CSV columns to data fields</li>
                                    </ul>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between mt-6">
                                    <button
                                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
                                        onClick={handleImportModalClose}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className={twMerge(
                                            'px-6 py-2 rounded text-white',
                                            (generalCsvFile || trailersCsvFile)
                                                ? 'bg-blue-600 hover:bg-blue-500'
                                                : 'bg-gray-400 cursor-not-allowed'
                                        )}
                                        onClick={handleContinueToConfiguration}
                                        disabled={!generalCsvFile && !trailersCsvFile}
                                    >
                                        Continue
                                    </button>
                                </div>
                            </div>
                        ) : importStep === 2 ? (
                            <div className="space-y-6">
                                {/* Configuration Step */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                    <h3 className="font-medium text-blue-900 mb-2">Data Configuration</h3>
                                    <p className="text-sm text-blue-800">
                                        Configure how your data should be interpreted during import. These settings will be applied to all imported records.
                                    </p>
                                </div>

                                {/* Trailer Name Format (only if trailers file exists) */}
                                {trailersCsvFile && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h2 className="text-lg font-medium mb-3">Trailer Name Format</h2>
                                        <p className="text-gray-600 text-sm mb-4">
                                            Select how trailer names are formatted in your CSV file:
                                        </p>
                                        <div className="flex flex-col gap-3">
                                            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                                                <input
                                                    type="radio"
                                                    name="trailerNameFormat"
                                                    value="compound"
                                                    checked={trailerNameFormat === 'compound'}
                                                    onChange={(e) => setTrailerNameFormat(e.target.value as 'compound' | 'separate')}
                                                    className="mt-1 text-blue-600"
                                                />
                                                <div>
                                                    <span className="font-medium text-gray-900">Compound Format</span>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        Trailer name contains both SCAC and number in one field (e.g., "UPSS-12345")
                                                    </p>
                                                </div>
                                            </label>
                                            <label className="flex items-start gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                                                <input
                                                    type="radio"
                                                    name="trailerNameFormat"
                                                    value="separate"
                                                    checked={trailerNameFormat === 'separate'}
                                                    onChange={(e) => setTrailerNameFormat(e.target.value as 'compound' | 'separate')}
                                                    className="mt-1 text-blue-600"
                                                />
                                                <div>
                                                    <span className="font-medium text-gray-900">Separate Fields</span>
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        SCAC code and trailer number are in separate columns
                                                    </p>
                                                </div>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                {/* Date Format (only if trailers file exists) */}
                                {trailersCsvFile && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h2 className="text-lg font-medium mb-3">Date Format</h2>
                                        <p className="text-gray-600 text-sm mb-4">
                                            Select the date/time format used in your CSV file:
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {[
                                                { value: 'MM/DD/YYYY HH:mm', example: '09/29/2025 20:33' },
                                                { value: 'DD/MM/YYYY HH:mm', example: '29/09/2025 20:33' },
                                                { value: 'YYYY-MM-DD HH:mm', example: '2025-09-29 20:33' },
                                                { value: 'MM-DD-YYYY HH:mm', example: '09-29-2025 20:33' }
                                            ].map(format => (
                                                <label key={format.value} className="flex items-start gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                                                    <input
                                                        type="radio"
                                                        name="dateFormat"
                                                        value={format.value}
                                                        checked={dateFormat === format.value}
                                                        onChange={(e) => setDateFormat(e.target.value as typeof dateFormat)}
                                                        className="mt-1 text-blue-600"
                                                    />
                                                    <div>
                                                        <span className="font-medium text-gray-900">{format.value}</span>
                                                        <p className="text-xs text-gray-600 mt-1">Example: {format.example}</p>
                                                    </div>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Load Status Mappings (only if trailers file exists) */}
                                {trailersCsvFile && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h2 className="text-lg font-medium mb-3">Load Status Mappings</h2>
                                        <p className="text-gray-600 text-sm mb-4">
                                            Map the status values in your CSV to standardized system statuses. Add or modify mappings as needed:
                                        </p>
                                        <div className="space-y-3">
                                            {Object.entries(loadStatusMappings).map(([csvValue, systemValue], index) => (
                                                <div key={index} className="flex items-center gap-3">
                                                    <div className="flex-1">
                                                        <label className="text-xs text-gray-600 mb-1 block">CSV Value</label>
                                                        <input
                                                            type="text"
                                                            value={csvValue}
                                                            onChange={(e) => {
                                                                const newMappings = {...loadStatusMappings};
                                                                delete newMappings[csvValue];
                                                                newMappings[e.target.value.toUpperCase()] = systemValue;
                                                                setLoadStatusMappings(newMappings);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm uppercase"
                                                            placeholder="e.g., LOADING"
                                                        />
                                                    </div>
                                                    <div className="text-gray-400 font-bold">→</div>
                                                    <div className="flex-1">
                                                        <label className="text-xs text-gray-600 mb-1 block">System Status</label>
                                                        <input
                                                            type="text"
                                                            value={systemValue}
                                                            onChange={(e) => {
                                                                const newMappings = {...loadStatusMappings};
                                                                newMappings[csvValue] = e.target.value.toUpperCase();
                                                                setLoadStatusMappings(newMappings);
                                                            }}
                                                            className="w-full p-2 border border-gray-300 rounded text-sm uppercase"
                                                            placeholder="e.g., LOADING STARTED"
                                                        />
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            const newMappings = {...loadStatusMappings};
                                                            delete newMappings[csvValue];
                                                            setLoadStatusMappings(newMappings);
                                                        }}
                                                        className="text-red-600 hover:text-red-800 mt-5"
                                                        title="Remove mapping"
                                                    >
                                                        <Delete fontSize="small" />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                onClick={() => {
                                                    const newMappings = {...loadStatusMappings};
                                                    newMappings['NEW_STATUS'] = 'NEW_STATUS';
                                                    setLoadStatusMappings(newMappings);
                                                }}
                                                className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded border border-blue-300 text-sm"
                                            >
                                                <Add fontSize="small" />
                                                Add Status Mapping
                                            </button>
                                        </div>
                                        <div className="mt-4 bg-gray-50 border border-gray-200 rounded p-3">
                                            <p className="text-xs text-gray-600">
                                                <strong>Note:</strong> Status values from your CSV that match the "CSV Value" will be converted to the corresponding "System Status" during import.
                                                Common statuses: EMPTY, LOADED, LOADING, UNLOADING, LOADING STARTED, UNLOADING STARTED
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {/* Information Box */}
                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <h3 className="font-medium text-green-900 mb-2">Configuration Complete</h3>
                                    <p className="text-sm text-green-800">
                                        Review your configuration settings above. You can always come back to adjust these settings later.
                                    </p>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between mt-6">
                                    <button
                                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
                                        onClick={handleBackToUpload}
                                    >
                                        Back
                                    </button>
                                    <button
                                        className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded"
                                        onClick={handleContinueToMapping}
                                    >
                                        Continue to Field Mapping
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Trailer Field Mapping */}
                                {trailersCsvFile && trailersCsvHeaders.length > 0 && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h2 className="text-lg font-medium mb-4">Trailer Data Field Mapping</h2>
                                        
                                        <p className="text-gray-600 text-sm mb-4">
                                            Map your CSV columns to trailer data fields:
                                        </p>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {/* Conditional rendering based on trailer name format */}
                                            {trailerNameFormat === 'compound' ? (
                                                <div className="space-y-1">
                                                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                        Trailer Name (SCAC-Trailer Number) <span className="text-red-500">*</span>
                                                        {autoAppliedTrailerMappings.includes('trailer_name') && (
                                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                Auto-applied
                                                            </span>
                                                        )}
                                                    </label>
                                                    <Select
                                                        value={createDropdownOptions(trailersCsvHeaders, trailersSampleData).find(opt => opt.value === trailerFieldMappings.trailer_name) || null}
                                                        onChange={(selectedOption: any) => handleTrailerFieldMappingChange('trailer_name', selectedOption?.value || '')}
                                                        options={createDropdownOptions(trailersCsvHeaders, trailersSampleData)}
                                                        components={{ Option: CustomOption }}
                                                        className="text-sm"
                                                        placeholder="Select column..."
                                                        isClearable
                                                    />
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="space-y-1">
                                                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                            SCAC Code <span className="text-red-500">*</span>
                                                            {autoAppliedTrailerMappings.includes('scac') && (
                                                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                    Auto-applied
                                                                </span>
                                                            )}
                                                        </label>
                                                        <Select
                                                            value={createDropdownOptions(trailersCsvHeaders, trailersSampleData).find(opt => opt.value === trailerFieldMappings.scac) || null}
                                                            onChange={(selectedOption: any) => handleTrailerFieldMappingChange('scac', selectedOption?.value || '')}
                                                            options={createDropdownOptions(trailersCsvHeaders, trailersSampleData)}
                                                            components={{ Option: CustomOption }}
                                                            className="text-sm"
                                                            placeholder="Select column..."
                                                            isClearable
                                                        />
                                                    </div>
                                                    <div className="space-y-1">
                                                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                            Trailer Number <span className="text-red-500">*</span>
                                                            {autoAppliedTrailerMappings.includes('trailer_number') && (
                                                                <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                    Auto-applied
                                                                </span>
                                                            )}
                                                        </label>
                                                        <Select
                                                            value={createDropdownOptions(trailersCsvHeaders, trailersSampleData).find(opt => opt.value === trailerFieldMappings.trailer_number) || null}
                                                            onChange={(selectedOption: any) => handleTrailerFieldMappingChange('trailer_number', selectedOption?.value || '')}
                                                            options={createDropdownOptions(trailersCsvHeaders, trailersSampleData)}
                                                            components={{ Option: CustomOption }}
                                                            className="text-sm"
                                                            placeholder="Select column..."
                                                            isClearable
                                                        />
                                                    </div>
                                                </>
                                            )}

                                            {/* Other trailer fields */}
                                            {[
                                                { key: 'last_move_date', label: 'Last Move Date', required: true },
                                                { key: 'trailer_check_in_date', label: 'Trailer Check-In Date', required: true },
                                                { key: 'location', label: 'Location', required: true },
                                                { key: 'location_type', label: 'Location Type', required: true },
                                                { key: 'reference_number', label: 'Reference Number', required: true },
                                                { key: 'load_status', label: 'Load Status', required: true }
                                            ].map(field => (
                                                <div key={field.key} className="space-y-1">
                                                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                                        {autoAppliedTrailerMappings.includes(field.key) && (
                                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                Auto-applied
                                                            </span>
                                                        )}
                                                    </label>
                                                    <Select
                                                        value={createDropdownOptions(trailersCsvHeaders, trailersSampleData).find(opt => opt.value === trailerFieldMappings[field.key as keyof typeof trailerFieldMappings]) || null}
                                                        onChange={(selectedOption: any) => handleTrailerFieldMappingChange(field.key, selectedOption?.value || '')}
                                                        options={createDropdownOptions(trailersCsvHeaders, trailersSampleData)}
                                                        components={{ Option: CustomOption }}
                                                        className="text-sm"
                                                        placeholder="Select column..."
                                                        isClearable
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Structure Field Mapping */}
                                {generalCsvFile && generalCsvHeaders.length > 0 && (
                                    <div className="border border-gray-200 rounded-lg p-4">
                                        <h2 className="text-lg font-medium mb-4">Structure Data Field Mapping</h2>
                                        <p className="text-gray-600 text-sm mb-4">
                                            Map your CSV columns to facility structure fields:
                                        </p>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {[
                                                { key: 'location', label: 'Location', required: true },
                                                { key: 'location_type', label: 'Location Type', required: true }
                                            ].map(field => (
                                                <div key={field.key} className="space-y-1">
                                                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                                                        {field.label} {field.required && <span className="text-red-500">*</span>}
                                                        {autoAppliedStructureMappings.includes(field.key) && (
                                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                                                Auto-applied
                                                            </span>
                                                        )}
                                                    </label>
                                                    <Select
                                                        value={createDropdownOptions(generalCsvHeaders, generalSampleData).find(opt => opt.value === structureFieldMappings[field.key as keyof typeof structureFieldMappings]) || null}
                                                        onChange={(selectedOption: any) => handleStructureFieldMappingChange(field.key, selectedOption?.value || '')}
                                                        options={createDropdownOptions(generalCsvHeaders, generalSampleData)}
                                                        components={{ Option: CustomOption }}
                                                        className="text-sm"
                                                        placeholder="Select column..."
                                                        isClearable
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Purge Data Status */}
                                {purgeExistingData && (
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                        <h3 className="font-medium text-orange-900 mb-2">⚠️ Purge Existing Data Enabled</h3>
                                        <p className="text-sm text-orange-800">
                                            Existing data will be deleted before importing. This action cannot be undone.
                                        </p>
                                    </div>
                                )}

                                {/* Staging Area Location Type Selector */}
                                {false && (<div className="border border-gray-200 rounded-lg p-4">
                                    <h2 className="text-lg font-medium mb-3">Staging Area Location Type</h2>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Select which location type should be used as the staging area for incoming trailers:
                                    </p>
                                    <Select
                                        value={stagingAreaLocationType ? { value: stagingAreaLocationType, label: stagingAreaLocationType } : null}
                                        onChange={(selectedOption: any) => {
                                            setStagingAreaLocationType(selectedOption?.value || '');
                                        }}
                                        options={availableLocationTypes.map(type => ({ value: type, label: type }))}
                                        className="text-sm"
                                        placeholder={availableLocationTypes.length > 0 ? "Select staging area..." : "Map location_type field to see available types"}
                                        isClearable
                                        isDisabled={availableLocationTypes.length === 0}
                                    />
                                    {availableLocationTypes.length === 0 ? (
                                        <p className="text-xs text-gray-500 mt-2">
                                            <strong>Note:</strong> Available location types will appear here once you map the location_type field above.
                                        </p>
                                    ) : (
                                        <p className="text-xs text-gray-500 mt-2">
                                            <strong>Found {availableLocationTypes.length} location type(s)</strong> in your CSV files.
                                        </p>
                                    )}
                                </div>)}

                                {/* Unknown Location Type Selector */}
                                {false && (<div className="border border-gray-200 rounded-lg p-4">
                                    <h2 className="text-lg font-medium mb-3">Unknown Location Type</h2>
                                    <p className="text-gray-600 text-sm mb-4">
                                        Select which location type should be used when a trailer's location is unknown or cannot be determined:
                                    </p>
                                    <Select
                                        value={unknownLocationType ? {
                                            value: unknownLocationType,
                                            label: unknownLocationType === 'Temporary' ? 'Temporary (New)' : unknownLocationType
                                        } : null}
                                        onChange={(selectedOption: any) => setUnknownLocationType(selectedOption?.value || 'Temporary')}
                                        options={[
                                            { value: 'Temporary', label: 'Temporary (New)' },
                                            ...availableLocationTypes.map(type => ({ value: type, label: type }))
                                        ]}
                                        className="text-sm"
                                        placeholder="Select unknown location type..."
                                    />
                                    <p className="text-xs text-gray-500 mt-2">
                                        <strong>Default:</strong> "Temporary" - A new temporary location type will be created for unknown trailers.
                                    </p>
                                </div>)}

                                {/* Validation Warning */}
                                {!areAllRequiredFieldsMapped() && (
                                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                        <h3 className="font-medium text-red-900 mb-2">⚠️ Missing Required Fields</h3>
                                        <p className="text-sm text-red-800 mb-2">
                                            Please map all required fields before importing:
                                        </p>
                                        <ul className="text-sm text-red-800 space-y-1 ml-4">
                                            {getMissingFieldsList().map((field, index) => (
                                                <li key={index}>• {field}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {/* Information Box */}
                                <div className={areAllRequiredFieldsMapped() ? "bg-green-50 border border-green-200 rounded-lg p-4" : "bg-blue-50 border border-blue-200 rounded-lg p-4"}>
                                    <h3 className={areAllRequiredFieldsMapped() ? "font-medium text-green-900 mb-2" : "font-medium text-blue-900 mb-2"}>
                                        {areAllRequiredFieldsMapped() ? "✓ All Required Fields Mapped" : "Mapping Instructions"}
                                    </h3>
                                    <ul className={areAllRequiredFieldsMapped() ? "text-sm text-green-800 space-y-1" : "text-sm text-blue-800 space-y-1"}>
                                        <li>• All fields marked with <span className="text-red-500">*</span> are required and must be mapped</li>
                                        <li>• Select the CSV column that corresponds to each data field</li>
                                        <li>• Review the sample data shown in dropdowns to ensure correct mapping</li>
                                    </ul>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-between mt-6">
                                    <button
                                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
                                        onClick={handleBackToConfiguration}
                                        disabled={importing}
                                    >
                                        Back
                                    </button>
                                    <button
                                        className={twMerge(
                                            'px-6 py-2 rounded text-white',
                                            importing || !areAllRequiredFieldsMapped()
                                                ? 'bg-gray-400 cursor-not-allowed'
                                                : 'bg-green-600 hover:bg-green-500'
                                        )}
                                        onClick={handleImportData}
                                        disabled={importing || !areAllRequiredFieldsMapped()}
                                        title={!areAllRequiredFieldsMapped() ? 'Please map all required fields before importing' : ''}
                                    >
                                        {importing ? 'Importing...' : 'Import Data'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {/* Progress Tracking Modal */}
            {showProgressModal && currentJobId && (
                <Modal
                    open={showProgressModal}
                    onClose={() => {}} // Prevent closing by clicking outside
                    className="flex justify-center items-center"
                >
                    <div className="w-[600px] bg-white border p-6 relative">
                        <h1 className="text-xl font-semibold mb-6">Import Progress</h1>
                        
                        {jobStatus && (
                            <div className="space-y-4">
                                {/* Job Status */}
                                <div className="flex items-center gap-3">
                                    <div className={twMerge(
                                        'w-3 h-3 rounded-full',
                                        jobStatus.status === 'running' ? 'bg-blue-500 animate-pulse' :
                                        jobStatus.status === 'completed' ? 'bg-green-500' :
                                        jobStatus.status === 'failed' ? 'bg-red-500' :
                                        'bg-gray-500'
                                    )}></div>
                                    <span className="font-medium capitalize">{jobStatus.status}</span>
                                    <span className="text-sm text-gray-500">Job ID: {currentJobId}</span>
                                </div>

                                {/* Progress Bar */}
                                {jobStatus.progress !== undefined && (
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Progress</span>
                                            <span>{jobStatus.progress}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                                style={{ width: `${jobStatus.progress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                {/* Records Progress */}
                                {jobStatus.total_records && (
                                    <div className="text-sm text-gray-600">
                                        Processed: {jobStatus.processed_records || 0} / {jobStatus.total_records} records
                                    </div>
                                )}

                                {/* Message */}
                                {jobStatus.message && (
                                    <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded">
                                        {jobStatus.message}
                                    </div>
                                )}

                                {/* Errors */}
                                {jobStatus.errors && jobStatus.errors.length > 0 && (
                                    <div className="bg-red-50 border border-red-200 rounded p-3">
                                        <h4 className="font-medium text-red-900 mb-2">Errors:</h4>
                                        <ul className="text-sm text-red-800 space-y-1">
                                            {jobStatus.errors.map((error: string, index: number) => (
                                                <li key={index}>• {error}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Completion Results */}
                                {jobStatus.status === 'completed' && jobStatus.imported_records && (
                                    <div className="bg-green-50 border border-green-200 rounded p-3">
                                        <h4 className="font-medium text-green-900 mb-2">Import Results:</h4>
                                        {jobStatus.imported_records.map((record: any, index: number) => (
                                            <div key={index} className="text-sm text-green-800">
                                                <strong>{record.file_type.toUpperCase()}:</strong> {record.records_processed} records processed
                                                {record.errors && record.errors.length > 0 && (
                                                    <div className="ml-4 text-red-600">
                                                        Errors: {record.errors.join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Timestamps */}
                                {(jobStatus.started_at || jobStatus.completed_at) && (
                                    <div className="text-xs text-gray-500 space-y-1">
                                        {jobStatus.started_at && <div>Started: {new Date(jobStatus.started_at).toLocaleString()}</div>}
                                        {jobStatus.completed_at && <div>Completed: {new Date(jobStatus.completed_at).toLocaleString()}</div>}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex justify-between mt-6">
                            <button
                                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded"
                                onClick={handleProgressModalClose}
                            >
                                Close & Continue Working
                            </button>
                            
                            {jobStatus?.status === 'running' && (
                                <button
                                    className="bg-blue-600 hover:bg-blue-500 text-white px-6 py-2 rounded"
                                    onClick={handleWaitForCompletion}
                                >
                                    Wait for Completion
                                </button>
                            )}
                            
                            {['completed', 'failed', 'cancelled'].includes(jobStatus?.status) && (
                                <button
                                    className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded"
                                    onClick={handleProgressModalClose}
                                >
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default Facility;